import "./BuilderApp.css";
import { useState, useEffect, useRef } from "react";
import { BrowserRouter as Router, Routes, Route, useParams, useNavigate } from 'react-router-dom';
import generatePublishedBuild from './components/PublishedPreview/generatePublishedBuild';
import { buildApiUrl } from './config/api';
import Elements from './components/Elements/Elements';
import { createCartElements } from './components/CartComponent';

// Components
import Hierarchy from "./components/Hierarchy/Hierarchy";
import Preview from "./components/Preview/Preview";
import TopBar from "./components/TopBar/TopBar";
import Inspector from "./components/Inspector/Inspector"; // Added back
import AssetsBar from "./components/AssetsBar/AssetsBar";
import PagesSidebar from './components/PagesSidebar/PagesSidebar';
import CMSPanel from './components/CMS/CMSPanel';
import LiveBuild from './components/LiveBuild/LiveBuild';
import CMSContentElement from './components/CMS/CMSContentElement';
import LoadingScreen from './components/LoadingScreen/LoadingScreen';
import SectionsSidebar from './components/SectionsSidebar';
import { ClientComponentsSidebar } from './components/ClientComponents/indexClientComp';

// Constants
import { DEVICES, DEVICE_SIZES } from "./constants/breakpoints";
import { updateElementStyles as updateStyles } from './components/StyleManager/StyleManager';
import { v4 as uuidv4 } from 'uuid';
import { loadFontsInHead, removeCustomFonts } from './utils/fontHelper';

// Add this near the top of App.js, after imports
const elementCategories = [
  {
    title: 'Templates',
    items: [
      {
        name: 'Navbar',
        type: 'navbar-template',
        description: 'Navigation bar template'
      }
    ]
  },
  {
    title: 'Basic Elements',
    items: [
      { name: 'Div', type: 'div', description: 'Basic container element' },
      { name: 'Section', type: 'section', description: 'Semantic section container' },
      { name: 'Container', type: 'div', description: 'A container to group elements' }
    ]
  },
  // ... copy the rest of the categories from Elements.jsx but you can omit the icon property
  {
    title: 'Form Elements',
    items: [
      { name: 'Form', type: 'form', description: 'Form container for inputs' },
      { name: 'Input', type: 'input', description: 'Text input field' },
      { name: 'Textarea', type: 'textarea', description: 'Multi-line text input' },
      { name: 'Checkbox', type: 'checkbox', description: 'Checkbox input' },
      { name: 'Radio', type: 'radio', description: 'Radio button input' },
      { name: 'Dropdown', type: 'dropdown', description: 'Dropdown select menu' },
      { name: 'Label', type: 'label', description: 'Form label' }
    ]
  },
  {
    title: 'Embed',
    items: [
      { name: 'Embed Code', type: 'embed', description: 'Embed external code or script' }
    ]
  },
  // ... copy the rest of the categories from Elements.jsx but you can omit the icon property
];

function BuilderApp() {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(siteId ? true : false);

  // Add the helper functions that were in Home
  const showNotification = (message, type = "success") => {
    const notification = document.createElement("div");
    notification.className = `notification ${type}`;
    notification.textContent = message;
    notification.style.cssText = `
      position: fixed;
      right: 20px;
      top: 20px;
      background: ${type === "error" ? "#ff4444" : "#4CAF50"};
      color: white;
      padding: 10px 20px;
      border-radius: 4px;
      z-index: 1000;
    `;
    document.body.appendChild(notification);
    setTimeout(() => notification.remove(), 3000);
  };

  const initializeDefaultEditor = () => {
    setPages([{
      id: 'home',
      name: 'Home',
      isHome: true,
      elements: [],
      classStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      },
      combinedClassStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      }
    }]);
    setCurrentPageId('home');
    setElements([]);
    setClassStylesRegistry({
      [DEVICES.DESKTOP]: new Map(),
      [DEVICES.TABLET]: new Map(),
      [DEVICES.MOBILE]: new Map()
    });
    setCombinedClassStylesRegistry({
      [DEVICES.DESKTOP]: new Map(),
      [DEVICES.TABLET]: new Map(),
      [DEVICES.MOBILE]: new Map()
    });
  };

  // Move the useEffect for loading site data here
  useEffect(() => {
    if (siteId) {
      loadSiteData(siteId);
    }
  }, [siteId]);

  const [elements, setElements] = useState([]);
  const [selectedElement, setSelectedElement] = useState(null);
  const [sidebarView, setSidebarView] = useState(null); // 'code' or 'properties' or null
  const [showElementSidebar, setShowElementSidebar] = useState(false);
  const [showHierarchySidebar, setShowHierarchySidebar] = useState(false);
  const [showAssetsSidebar, setShowAssetsSidebar] = useState(false);
  const [showSectionsSidebar, setShowSectionsSidebar] = useState(false);
  const [selectedSectionCategory, setSelectedSectionCategory] = useState(null);
  const [showClientComponentsSidebar, setShowClientComponentsSidebar] = useState(false);
  const [assets, setAssets] = useState([]);
  const [selectedAssetFile, setSelectedAssetFile] = useState(null);
  const [selectedDevice, setSelectedDevice] = useState(DEVICES.DESKTOP);
  const [hasManualResize, setHasManualResize] = useState(false);
  const [globalClassNames, setGlobalClassNames] = useState(new Set());
  const [classStylesRegistry, setClassStylesRegistry] = useState({
    [DEVICES.DESKTOP]: new Map(),
    [DEVICES.TABLET]: new Map(),
    [DEVICES.MOBILE]: new Map()
  });
  const [combinedClassStylesRegistry, setCombinedClassStylesRegistry] = useState({
    [DEVICES.DESKTOP]: new Map(),
    [DEVICES.TABLET]: new Map(),
    [DEVICES.MOBILE]: new Map()
  });
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [elementInteractions, setElementInteractions] = useState({});
  const [appliedStylesRegistry, setAppliedStylesRegistry] = useState({
    [DEVICES.DESKTOP]: new Map(),
    [DEVICES.TABLET]: new Map(),
    [DEVICES.MOBILE]: new Map()
  });
  const [previewUrl, setPreviewUrl] = useState(null);
  const [pages, setPages] = useState([
    {
      id: 'home',
      name: 'Home',
      isHome: true,
      cartPosition: 'under-button-left',
      elements: [],
      classStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      },
      combinedClassStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      }
    }
  ]);
  const [currentPageId, setCurrentPageId] = useState('home');
  const [showPagesSidebar, setShowPagesSidebar] = useState(false);
  const [showCMSPanel, setShowCMSPanel] = useState(false);
  const [selectedContentItem, setSelectedContentItem] = useState(null);
  const [contentItems, setContentItems] = useState(null);
  const [showLiveBuild, setShowLiveBuild] = useState(false);

  // Add new state for CMS data
  const [cmsContentTypes, setCmsContentTypes] = useState([]);
  const [cmsContent, setCmsContent] = useState({});  // Object to store content by type ID

  // Add this near your other state declarations in BuilderApp
  const [newlyAddedElement, setNewlyAddedElement] = useState(null);
  const [showRichTextEditorForNewElement, setShowRichTextEditorForNewElement] = useState(false);

  const [zoomLevel, setZoomLevel] = useState(0.90);
  const [siteFonts, setSiteFonts] = useState([]); // Add state for site fonts

  useEffect(() => {
    console.log("App.js Selected Element:", selectedElement);

    //Currently setting this here temporarily but it can be added in Style Managher for single classes section.
    if (selectedElement && selectedElement.id) {
      /* setElements(prevElements => 
         prevElements.map(element => 
           element.id === selectedElement.id 
             ? { ...element, styles: { ...selectedElement.styles } }
             : element
         )
       ); */
    }
  }, [selectedElement]);

  useEffect(() => {
    // Update selected element if any
    // if (selectedElement?.className) {
    //   setSelectedElement(prev => {
    //     const classNames = prev.className.split(' ');
    //     const combinedStyles = {};

    //     classNames.forEach(className => {
    //       const cascadedStyles = getCascadedStyles(className, selectedDevice);
    //       Object.assign(combinedStyles, cascadedStyles);
    //     });

    //     if (classNames.length > 1) {
    //       // Use original order here as well
    //       const combinationKey = classNames.join('.');
    //       const cascadedCombinedStyles = getCascadedStyles(combinationKey, selectedDevice);
    //       Object.assign(combinedStyles, cascadedCombinedStyles);
    //     }

    //     return {
    //       ...prev,
    //       styles: combinedStyles
    //     };
    //   });
    // }
  }, [selectedElement]);

  useEffect(() => {
    // console.log("App.js Selected Device:", selectedDevice);

  }, [selectedDevice]);

  useEffect(() => {
    console.log("App.js Class Styles Registry:", classStylesRegistry);
  }, [classStylesRegistry]);

  useEffect(() => {
    console.log("App.js Combined Class Styles Registry:", combinedClassStylesRegistry);
  }, [combinedClassStylesRegistry]);

  useEffect(() => {
    // console.log("App.js Class Styles Registry:", classStylesRegistry);

  }, [pages]);

  useEffect(() => {
    console.log("Elements:", elements);
  }, [elements]);

  useEffect(() => {
    const getAssets = async () => {
      try {
        const siteId = window.location.pathname.split('/editor/')[1];
        const response = await fetch(
          buildApiUrl(`api/sites/${siteId}/assets`),
          {
            method: "GET",
          }
        );
        const data = await response.json();
        const sortedAssets = data.assets.sort(
          (a, b) => new Date(b.uploadedAt) - new Date(a.uploadedAt)
        );
        setAssets(sortedAssets);
      } catch (error) {
        console.error("Error fetching assets:", error);
      }
    };
    getAssets();
  }, []);


  // Default styles based on element type
  const getDefaultStyles = (elementType, element) => {
    if (element) {
      return element.styles;
    }
    // return selectedElement.styles;
    const baseStyles = {
      position: 'relative',
      minHeight: '40px',
      minWidth: '40px'
    };

    switch (elementType) {
      case 'div':
        return {
          ...baseStyles,
          display: 'block',
          paddingTop: '15px',
          paddingRight: '15px',
          paddingBottom: '15px',
          paddingLeft: '15px'
        };
      case 'section':
        return {
          ...baseStyles,
          width: '100%',
          paddingTop: '20px',
          paddingRight: '20px',
          paddingBottom: '20px',
          paddingLeft: '20px'
        };
      case 'h1':
        return {
          ...baseStyles,
          fontSize: '24px',
          fontWeight: 'bold',
          paddingTop: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          paddingLeft: '10px'
        };
      case 'h2':
        return {
          ...baseStyles,
          fontSize: '22px',
          fontWeight: 'bold',
          paddingTop: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          paddingLeft: '10px'
        };
      case 'h3':
        return {
          ...baseStyles,
          fontSize: '18px',
          fontWeight: 'bold',
          paddingTop: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          paddingLeft: '10px'
        };
      case 'h4':
        return {
          ...baseStyles,
          fontSize: '16px',
          fontWeight: 'bold',
          paddingTop: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          paddingLeft: '10px'
        };
      case 'h5':
        return {
          ...baseStyles,
          fontSize: '14px',
          fontWeight: 'bold',
          paddingTop: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          paddingLeft: '10px'
        };
      case 'h6':
        return {
          ...baseStyles,
          fontSize: '12px',
          fontWeight: 'bold',
          paddingTop: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          paddingLeft: '10px'
        };
      case 'p':
        return {
          ...baseStyles,
          fontSize: '16px',
          lineHeight: '1.5',
          paddingTop: '15px',
          paddingRight: '15px',
          paddingBottom: '15px',
          paddingLeft: '15px'
        };
      case 'button':
        return {
          paddingTop: '8px',
          paddingRight: '16px',
          paddingBottom: '8px',
          paddingLeft: '16px',
          backgroundColor: '#007bff',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer'
        };
      case 'dropdown':
        return {
          ...baseStyles,
          display: 'block',
          width: '100%',
          maxWidth: '300px',
          height: '38px',
          padding: '6px 12px',
          fontSize: '14px',
          color: '#333',
          backgroundColor: '#fff',
          backgroundImage: 'none',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: 'inset 0 1px 1px rgba(0, 0, 0, 0.075)',
          transition: 'border-color ease-in-out .15s, box-shadow ease-in-out .15s'
        };
      case 'link':
        return {
          color: '#007bff',
          textDecoration: 'none',
          cursor: 'pointer',
          paddingTop: '5px',
          paddingRight: '5px',
          paddingBottom: '5px',
          paddingLeft: '5px'
        };
      case 'link-block':
        return {
          // ...baseStyles,
          display: 'block',
          // cursor: 'pointer',
          textDecoration: 'none',
          // paddingTop: '15px',
          // paddingRight: '15px',
          // paddingBottom: '15px',
          // paddingLeft: '15px'
        };
      case 'image':
        return {
          maxWidth: '100%',
          height: 'auto',
          display: 'block',
          verticalAlign: 'top',
          // Remove any default background image
          // Let the Inspector/asset selection handle this
        };
      case 'video':
        return {
          width: '100%',
          height: '400px',
          minHeight: '315px',
          paddingTop: '10px',
          paddingRight: '10px',
          paddingBottom: '10px',
          paddingLeft: '10px',
          border: 'none',
          borderRadius: '4px',
          boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          backgroundColor: '#f8f8f8',
          overflow: 'hidden'
        };
      case 'grid':
        return {
          ...baseStyles,
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '20px',
          paddingTop: '20px',
          paddingRight: '20px',
          paddingBottom: '20px',
          paddingLeft: '20px'
        };
      case 'input':
        return {
          ...baseStyles,
          display: 'block',
          width: '100%',
          padding: '8px 12px',
          fontSize: '16px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          marginBottom: '15px',
          boxSizing: 'border-box'
        };
      case 'checkbox':
      case 'radio':
        return {
          margin: '4px'
        };
      case 'rich-text':
        return {
          ...baseStyles,
          paddingTop: '12px',
          paddingRight: '12px',
          paddingBottom: '12px',
          paddingLeft: '12px',
          backgroundColor: '#ffffff',
          color: '#333333',
          border: '1px solid #dddddd',
          borderRadius: '4px',
          minHeight: '100px'
        };
      case 'label':
        return {
          // ...baseStyles,
          display: 'block',
          marginBottom: '8px',
          fontSize: '16px',
          color: '#333333',
          fontWeight: '500'
        };
      case 'textarea':
        return {
          ...baseStyles,
          display: 'block',
          width: '100%',
          padding: '8px 12px',
          fontSize: '16px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          minHeight: '120px',
          resize: 'vertical',
          boxSizing: 'border-box',
        };
      case 'submit':
        return {
          ...baseStyles,
          display: 'inline-block',
          padding: '8px 16px',
          backgroundColor: '#007bff',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '16px',
          fontWeight: '500',
          minWidth: '120px'
        };
      case 'text':
        return {
          ...baseStyles,
          fontSize: '16px',
          color: 'inherit',
          margin: '0'
        };
      case 'form':
        return {
          ...baseStyles,
          display: 'flex',
          flexDirection: 'column',
          gap: '15px',
          width: '100%',
          // padding: '20px'
        };
      case 'cart':
        return {
          position: 'relative',
        };
      case 'cart-button':
        return {
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 16px',
          backgroundColor: '#2a2a2a',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500'
        };
      case 'map':
        return {
          ...baseStyles,
          width: '100%',
          height: '400px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          backgroundColor: '#f8f8f8'
        };
      case 'embed':
        return {
        };
      default:
        return baseStyles;
    }
  };


  const addNewElement = (type, parentId = null) => {
    // Get the hierarchyName based on element type
    const getHierarchyName = (elementType) => {
      // Find the element definition in elementCategories
      for (const category of elementCategories) {
        const element = category.items.find(item => item.type === elementType);
        if (element) {
          return element.name;
        }
      }
      return elementType; // Fallback to type if name not found
    };

    const validParentTypes = {
      'div': ["div", "h1", "h2", "h3", "h4", "h5", "h6", "p", "button", "list", "image", "section", "link", "text", "video", "carousel", "map", "grid", "input", "checkbox", "radio", "label", "textarea", "submit", "cart", "rich-text", "embed", "link-block", "dropdown"],
      'section': ["div", "h1", "h2", "h3", "h4", "h5", "h6", "p", "button", "list", "image", "section", "link", "text", "video", "carousel", "map", "grid", "input", "checkbox", "radio", "label", "textarea", "submit", "cart", "rich-text", "embed", "link-block", "dropdown"],
      'form': ["div", "input", "textarea", "label", "submit", "button", "checkbox", "radio", "dropdown"],
      'listItem': ["div", "h1", "p", "button", "image", "link"], // list items can contain these elements
      'link': ["text", "image", "button"], // links can contain text, images, or buttons
      'input': [], // input can't contain elements
      'checkbox': [], // checkbox can't contain elements
      'radio': [], // radio can't contain elements
      'dropdown': ["option"], // dropdown can only contain option elements
      'option': [], // option can't contain other elements
      'grid': ["div", "section"], // grid can only contain divs and sections
      'cart': ["cart-item", "cart-total", "checkout-button"], // cart can only contain cart items, cart total, and checkout button
      'cart-content': ["div", "h1", "h2", "h3", "h4", "h5", "h6", "p", "button", "list", "image", "section", "link", "text", "video", "carousel", "map", "grid", "input", "checkbox", "radio", "label", "textarea", "submit"], // cart can only contain cart items, cart total, and checkout button
    };

    // Helper function to show notification
    const showNotification = (message, type = "error") => {
      const notification = document.createElement("div");
      notification.className = `notification ${type}`;
      notification.textContent = message;
      notification.style.cssText = `
        position: fixed;
        right: 20px;
        top: 20px;
        background: ${type === "error" ? "#ff4444" : "#007bff"};
        color: white;
        padding: 10px 20px;
        border-radius: 4px;
        z-index: 1000;
      `;
      document.body.appendChild(notification);
      setTimeout(() => notification.remove(), 3000);
    };

    // Helper function to find valid parent
    const findValidParent = (currentElement) => {
      if (!currentElement) return null;

      const allowedChildren = validParentTypes[currentElement.type] || [];
      if (allowedChildren.includes(type)) {
        return currentElement;
      }

      const parentElement = elements.find(el => el.id === currentElement.parentId);
      return findValidParent(parentElement);
    };

    // Find the valid parent starting from selected element
    const validParent = selectedElement ? findValidParent(selectedElement) : null;

    // Validate parent-child relationships
    if (selectedElement) {
      if (validParentTypes[selectedElement.type] === undefined) {
        showNotification(`${selectedElement.type} cannot contain other elements`);
        return;
      }

      if (!validParentTypes[selectedElement.type].includes(type)) {
        showNotification(`Cannot add ${type} inside ${selectedElement.type}`);
        return;
      }
    }

    const builderId = `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;



    // Get default text content based on element type
    const getDefaultText = (elementType) => {
      switch (elementType) {
        case 'h1':
          return 'Heading 1';
        case 'h2':
          return 'Heading 2';
        case 'h3':
          return 'Heading 3';
        case 'h4':
          return 'Heading 4';
        case 'h5':
          return 'Heading 5';
        case 'h6':
          return 'Heading 6';
        case 'p':
          return 'Paragraph text';
        case 'button':
          return 'Button';
        case 'link':
          return 'Link';
        case 'link-block':
          return 'Link Block (Anchor Link)';
        case 'text':
          return 'Text';
        case 'rich-text':
          return '<p>This is a rich text editor. You can add formatting, lists and more.</p>';
        case 'label':
          return 'Label';
        case 'textarea':
          return 'Enter text here...';
        case 'submit':
          return 'Submit';
        case 'dropdown':
          return '<option value="">Select an option</option><option value="option1">Option 1</option><option value="option2">Option 2</option><option value="option3">Option 3</option>';
        case 'map':
          return 'Map Component';
        case 'video':
          return 'Video Component';
        case 'embed':
          return 'Embed Code (Will render on published site)';
        default:
          return '';
      }
    };

    if (type === 'form-block') {
      // Create unique IDs for all elements
      const formBlockId = Date.now();
      const formId = formBlockId + 1;
      const nameLabel = formBlockId + 2;
      const nameInput = formBlockId + 3;
      const emailLabel = formBlockId + 4;
      const emailInput = formBlockId + 5;
      const submitId = formBlockId + 6;
      const successDivId = formBlockId + 7;
      const successTextId = formBlockId + 8;
      const errorDivId = formBlockId + 9;
      const errorTextId = formBlockId + 10;

      // Create form block (div container)
      const formBlockElement = {
        id: formBlockId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        hierarchyName: 'Form Block Container',
        formName: 'Email Form',
        styles: getDefaultStyles('div'),
        children: [formId, successDivId, errorDivId],
        parentId: validParent ? validParent.id : null,
        visibility: 'visible',
        isExpanded: true  // Add this property
      };

      // Create form element
      const formElement = {
        id: formId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'form',
        hierarchyName: 'Email Form',
        formName: 'Email Form',
        method: 'get',
        styles: getDefaultStyles('form'),
        children: [nameLabel, nameInput, emailLabel, emailInput, submitId],
        parentId: formBlockId,
        visibility: 'visible',
        successDivId: successDivId,
        errorDivId: errorDivId,
        siteId: '1737280808387'
      };

      // Create name label element
      const nameLabelElement = {
        id: nameLabel,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'label',
        hierarchyName: 'Name Label',
        text: 'Name:',
        styles: getDefaultStyles('label'),
        children: [],
        parentId: formId,
        visibility: 'visible'
      };

      // Create name input element
      const nameInputElement = {
        id: nameInput,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'input',
        hierarchyName: 'Name Input',
        inputType: 'text',
        name: 'name',
        placeholder: 'Enter your name',
        styles: getDefaultStyles('input'),
        children: [],
        parentId: formId,
        visibility: 'visible'
      };

      // Create email label element
      const emailLabelElement = {
        id: emailLabel,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'label',
        hierarchyName: 'Email Label',
        text: 'Email:',
        styles: getDefaultStyles('label'),
        children: [],
        parentId: formId,
        visibility: 'visible'
      };

      // Create email input element
      const emailInputElement = {
        id: emailInput,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'input',
        hierarchyName: 'Email Input',
        inputType: 'email',
        name: 'email',
        placeholder: 'Enter your email',
        styles: getDefaultStyles('input'),
        children: [],
        parentId: formId,
        visibility: 'visible'
      };

      // Create submit button element
      const submitElement = {
        id: submitId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'submit',
        hierarchyName: 'Submit Button',
        text: 'Submit',
        styles: getDefaultStyles('submit'),
        children: [],
        parentId: formId,
        visibility: 'visible'
      };

      // Create success message container
      const successDivElement = {
        id: successDivId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        styles: {
          display: 'none',
          padding: '10px',
          marginTop: '15px',
          backgroundColor: '#d4edda',
          borderRadius: '4px',
          color: '#155724',
          border: '1px solid #c3e6cb'
        },
        children: [successTextId],
        parentId: formBlockId,
        visibility: 'visible',
        attributes: {
          'data-element': 'form-success-message'
        }
      };

      // Create success message text
      const successTextElement = {
        id: successTextId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'text',
        text: 'Form submitted successfully!',
        styles: getDefaultStyles('text'),
        children: [],
        parentId: successDivId,
        visibility: 'visible'
      };

      // Create error message container
      const errorDivElement = {
        id: errorDivId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        styles: {
          display: 'none',
          padding: '10px',
          marginTop: '15px',
          backgroundColor: '#f8d7da',
          borderRadius: '4px',
          color: '#721c24',
          border: '1px solid #f5c6cb'
        },
        children: [errorTextId],
        parentId: formBlockId,
        visibility: 'visible',
        attributes: {
          'data-element': 'form-error-message'
        }
      };

      // Create error message text
      const errorTextElement = {
        id: errorTextId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'text',
        text: 'Error submitting form. Please try again.',
        styles: getDefaultStyles('text'),
        children: [],
        parentId: errorDivId,
        visibility: 'visible'
      };

      // Add all elements
      setElements(prevElements => [
        ...prevElements,
        formBlockElement,
        formElement,
        nameLabelElement,
        nameInputElement,
        emailLabelElement,
        emailInputElement,
        submitElement,
        successDivElement,
        successTextElement,
        errorDivElement,
        errorTextElement
      ]);

      // Select the form block element
      setSelectedElement(formBlockElement);
      setNewlyAddedElement(formBlockElement);
      return;
    }

    if (type === 'dropdown') {
      // Create unique IDs for all elements
      const dropdownId = Date.now();

      // Create IDs for option elements
      const option1Id = dropdownId + 1;
      const option2Id = dropdownId + 2;
      const option3Id = dropdownId + 3;

      // Create dropdown container element
      const dropdownElement = {
        id: dropdownId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'dropdown',
        hierarchyName: 'Dropdown',
        styles: getDefaultStyles('dropdown'),
        children: [option1Id, option2Id, option3Id],
        parentId: validParent ? validParent.id : null,
        visibility: 'visible',
        isExpanded: true
      };

      // Create option elements
      const option1Element = {
        id: option1Id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'option',
        hierarchyName: 'Option 1',
        text: 'Option 1',
        value: 'option1',
        parentId: dropdownId,
        visibility: 'visible'
      };

      const option2Element = {
        id: option2Id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'option',
        hierarchyName: 'Option 2',
        text: 'Option 2',
        value: 'option2',
        parentId: dropdownId,
        visibility: 'visible'
      };

      const option3Element = {
        id: option3Id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'option',
        hierarchyName: 'Option 3',
        text: 'Option 3',
        value: 'option3',
        parentId: dropdownId,
        visibility: 'visible'
      };

      // Add all elements to the elements array
      setElements(prevElements => [
        ...prevElements,
        dropdownElement,
        option1Element,
        option2Element,
        option3Element
      ]);

      // Select the dropdown element
      setSelectedElement(dropdownElement);
      setNewlyAddedElement(dropdownElement);
      return;
    }

    if (type === 'navbar-template') {
      // Define class names
      const navbarClass = 'navbar-container';
      const logoClass = 'navbar-logo';
      const navLinksClass = 'navbar-links';
      const navLinkClass = 'nav-link';
      const menuBtnClass = 'navbar-menu-btn';

      // Define styles for desktop
      const navbarStyles = {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingTop: '16px',
        paddingRight: '24px',
        paddingBottom: '16px',
        paddingLeft: '24px',
        backgroundColor: '#333',
        color: '#fff',
        width: '100%',
        boxSizing: 'border-box',
        maxWidth: '100%',
        margin: '0',
        position: 'relative'
      };

      const logoStyles = {
        fontSize: '24px',
        fontWeight: 'bold',
        color: '#fff',
        textDecoration: 'none'
      };

      const navLinksStyles = {
        display: 'flex',
        gap: '2rem',
        alignItems: 'center'
      };

      const navLinkStyles = {
        color: '#fff',
        textDecoration: 'none',
        fontSize: '16px',
        transition: 'color 0.3s ease'
      };

      const menuBtnStyles = {
        display: 'none',
        background: 'transparent',
        border: 'none',
        color: '#fff',
        fontSize: '24px',
        cursor: 'pointer',
        padding: '0.5rem'
      };

      const mobileMenuBtnStyles = {
        display: 'block',
      };

      // Update the mobile-specific styles for nav links
      const mobileNavLinksStyles = {
        ...navLinksStyles,
        display: 'none',
        position: 'absolute',
        top: '100%',
        left: 0,
        right: 0,
        flexDirection: 'column',
        backgroundColor: '#333',
        gap: '1rem',
        zIndex: '1000'
      };

      // Update the mobile link styles to take full width
      const mobileLinkStyles = {
        ...navLinkStyles,
        width: '100%',
        textAlign: 'center',
        padding: '0.5rem 0'
      };


      // Update the classStylesRegistry to include mobile link styles
      setClassStylesRegistry(prev => {
        const newRegistry = { ...prev };

        // Desktop styles
        newRegistry[DEVICES.DESKTOP].set(navbarClass, navbarStyles);
        newRegistry[DEVICES.DESKTOP].set(logoClass, logoStyles);
        newRegistry[DEVICES.DESKTOP].set(navLinksClass, navLinksStyles);
        newRegistry[DEVICES.DESKTOP].set(navLinkClass, navLinkStyles);
        newRegistry[DEVICES.DESKTOP].set(menuBtnClass, menuBtnStyles);
        // Tablet styles
        // newRegistry[DEVICES.TABLET].set(navbarClass, navbarStyles);
        // Mobile styles
        newRegistry[DEVICES.MOBILE].set(navLinksClass, mobileNavLinksStyles);
        newRegistry[DEVICES.MOBILE].set(navLinkClass, mobileLinkStyles);
        newRegistry[DEVICES.MOBILE].set(menuBtnClass, mobileMenuBtnStyles);
        return newRegistry;
      });

      // Add classes to globalClassNames
      setGlobalClassNames(prev => new Set([...prev, navbarClass, logoClass, navLinksClass, navLinkClass, menuBtnClass]));



      // Create navbar container
      const navbarId = Date.now();

      // Create IDs for all elements first to avoid reference errors
      const logoId = navbarId + 1;
      const menuBtnId = navbarId + 2;
      const navLinksId = navbarId + 3;

      // Create navbar container
      const navbarElement = {
        id: navbarId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        className: navbarClass,
        text: '',
        styles: {},
        children: [],
        parentId: validParent ? validParent.id : null,
        visibility: 'visible'
      };

      // Create logo container
      const logoElement = {
        id: logoId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'link',
        className: logoClass,
        text: 'Logo',
        styles: {},
        children: [],
        parentId: navbarId,
        visibility: 'visible'
      };

      // Create menu button
      const menuBtnElement = {
        id: menuBtnId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'button',
        className: menuBtnClass,
        text: '☰',
        styles: {},
        children: [],
        parentId: navbarId,
        visibility: 'visible',
        // Add interaction events for mobile menu toggle functionality
        // First click shows the menu by setting display: flex
        // Second click hides the menu by setting display: none
        interactions: {
          events: [
            {
              id: Date.now() + 100,
              type: 'click',
              action: 'toggle',
              sequence: 'first',
              label: '1st Click: Toggle Display (flex): navbar-links',
              value: {
                targetId: navLinksId,
                display: 'flex'
              }
            },
            {
              id: Date.now() + 101,
              type: 'click',
              action: 'toggle',
              sequence: 'second',
              label: '2nd Click: Toggle Display (none): navbar-links',
              value: {
                targetId: navLinksId,
                display: 'none'
              }
            }
          ]
        }
      };

      // Create nav links container
      const navLinksElement = {
        id: navLinksId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        className: navLinksClass,
        text: '',
        styles: {},
        children: [],
        parentId: navbarId,
        visibility: 'visible'
      };

      // Create nav links
      const links = [
        { text: 'Home', href: '#' },
        { text: 'About', href: '#' },
        { text: 'Services', href: '#' },
        { text: 'Contact', href: '#' }
      ];

      const linkElements = links.map((link, index) => ({
        id: navLinksId + 1 + index,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'link',
        className: navLinkClass,
        text: link.text,
        styles: {},
        children: [],
        parentId: navLinksId,
        visibility: 'visible'
      }));

      // Update navbar's children to include menu button
      navbarElement.children = [logoId, menuBtnId, navLinksId];
      navLinksElement.children = linkElements.map(link => link.id);

      // Add all elements
      setElements(prevElements => [
        ...prevElements,
        navbarElement,
        logoElement,
        menuBtnElement,
        navLinksElement,
        ...linkElements
      ]);




      // Select the navbar element
      // setSelectedElement(navbarElement);
      return;
    }

    if (type === 'tabs') {
      // Create unique IDs for all elements
      const tabsId = Date.now();
      const tabMenuId = tabsId + 1;
      const tabContentId = tabsId + 2;

      // Create unique group identifier for this tab set
      const tabGroupId = `tab-group-${tabsId}`;

      // Create IDs for tab links
      const tab1LinkId = tabsId + 3;
      const tab2LinkId = tabsId + 4;
      const tab3LinkId = tabsId + 5;

      // Create IDs for tab content divs
      const tab1ContentId = tabsId + 6;
      const tab2ContentId = tabsId + 7;
      const tab3ContentId = tabsId + 8;

      // Define default styles for 'Tab' class
      const tabStyles = {
        padding: '8px 16px',
        border: 'none',
        backgroundColor: 'transparent',
        color: '#333',
        cursor: 'pointer',
        borderRadius: '4px'
      };

      // Define styles for 'active' class
      const activeStyles = {
        backgroundColor: '#007bff',
        color: '#fff'
      };

      // Update classStylesRegistry with 'Tab' class
      setClassStylesRegistry(prev => {
        const newRegistry = { ...prev };

        newRegistry[DEVICES.DESKTOP].set('Tab', tabStyles);
        newRegistry[DEVICES.DESKTOP].set('active', activeStyles);

        newRegistry[DEVICES.TABLET].set('Tab', tabStyles);
        newRegistry[DEVICES.TABLET].set('active', activeStyles);

        newRegistry[DEVICES.MOBILE].set('Tab', tabStyles);
        newRegistry[DEVICES.MOBILE].set('active', activeStyles);

        return newRegistry;
      });

      // Update combinedClassStylesRegistry for Tab+active
      setCombinedClassStylesRegistry(prev => {
        const newRegistry = { ...prev };
        const combinedStyles = {
          ...tabStyles,
          ...activeStyles
        };

        newRegistry[DEVICES.DESKTOP].set('Tab+active', combinedStyles);
        newRegistry[DEVICES.TABLET].set('Tab+active', combinedStyles);
        newRegistry[DEVICES.MOBILE].set('Tab+active', combinedStyles);

        return newRegistry;
      });

      // Add classes to globalClassNames
      setGlobalClassNames(prev => new Set([...prev, 'Tab', 'active']));

      // Create main tabs container
      const tabsElement = {
        id: tabsId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        styles: {
          display: 'flex',
          flexDirection: 'column',
          gap: '20px',
          width: '100%',
          maxWidth: '600px',
          margin: '0 auto'
        },
        children: [tabMenuId, tabContentId],
        parentId: validParent ? validParent.id : null,
        visibility: 'visible'
      };

      // Create tabs menu container
      const tabMenuElement = {
        id: tabMenuId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        styles: {
          display: 'flex',
          gap: '10px',
          borderBottom: '1px solid #ddd',
          paddingBottom: '10px'
        },
        children: [tab1LinkId, tab2LinkId, tab3LinkId],
        parentId: tabsId,
        visibility: 'visible'
      };

      // Create tab links with classes, content relationships and group ID
      const createTabLink = (id, text, index, contentId) => ({
        id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'button',
        text,
        className: index === 0 ? 'Tab active' : 'Tab',
        styles: {},
        children: [],
        parentId: tabMenuId,
        visibility: 'visible',
        relatedContentId: contentId,
        tabGroupId: tabGroupId, // Add group identifier
        byDefault: index === 0
      });

      const tabLinks = [
        createTabLink(tab1LinkId, 'Tab 1', 0, tab1ContentId),
        createTabLink(tab2LinkId, 'Tab 2', 1, tab2ContentId),
        createTabLink(tab3LinkId, 'Tab 3', 2, tab3ContentId)
      ];

      // Create tabs content container
      const tabContentElement = {
        id: tabContentId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        styles: {
          padding: '20px 0'
        },
        children: [tab1ContentId, tab2ContentId, tab3ContentId],
        parentId: tabsId,
        visibility: 'visible'
      };

      // Create tab content divs with group ID
      const createTabContent = (id, text, index) => ({
        id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        text,
        styles: {
          display: index === 0 ? 'block' : 'none',
          padding: '20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px'
        },
        children: [],
        parentId: tabContentId,
        visibility: 'visible',
        tabGroupId: tabGroupId // Add group identifier
      });

      const tabContents = [
        createTabContent(tab1ContentId, 'Tab 1 Content', 0),
        createTabContent(tab2ContentId, 'Tab 2 Content', 1),
        createTabContent(tab3ContentId, 'Tab 3 Content', 2)
      ];

      // Add all elements
      setElements(prevElements => [
        ...prevElements,
        tabsElement,
        tabMenuElement,
        tabContentElement,
        ...tabLinks,
        ...tabContents
      ]);

      // Select the tabs container element
      setSelectedElement(tabsElement);
      return;
    }

    if (type === 'slider') {
      const sliderId = Date.now();
      const maskId = sliderId + 1;
      const slide1Id = sliderId + 2;
      const slide2Id = sliderId + 3;
      const slide3Id = sliderId + 4;
      const leftArrowId = sliderId + 5;
      const rightArrowId = sliderId + 6;

      // Create slider container
      const sliderElement = {
        id: sliderId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'slider',
        hierarchyName: 'Slider Container',
        sliderRole: 'container',
        slidesPerView: 1,
        slidesPerScroll: 1,
        deviceSettings: {
          desktop: {
            slidesPerView: 1,
            slidesPerScroll: 1
          },
          tablet: {
            slidesPerView: 1,
            slidesPerScroll: 1
          },
          mobile: {
            slidesPerView: 1,
            slidesPerScroll: 1
          }
        },
        styles: {
          position: 'relative',
          width: '100%',
          height: '400px',
          overflow: 'hidden',
          backgroundColor: '#f5f5f5'
        },
        children: [maskId, leftArrowId, rightArrowId],
        parentId: validParent ? validParent.id : null,
        visibility: 'visible'
      };

      // Create mask container
      const maskElement = {
        id: maskId,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        hierarchyName: 'Slider Mask',
        sliderRole: 'mask',
        styles: {
          position: 'absolute',
          display: 'flex',
          width: '100%',
          height: '100%',
          transition: 'transform 0.5s ease-in-out'
        },
        children: [slide1Id, slide2Id, slide3Id],
        parentId: sliderId,
        visibility: 'visible'
      };

      // Create slides
      const createSlide = (id, index) => ({
        id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'div',
        hierarchyName: `Slide ${index}`,
        sliderRole: 'slide',
        styles: {
          flex: '0 0 100%', // Will be adjusted based on slidesPerView
          height: '100%',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          position: 'relative',
          fontSize: '24px',
          color: '#666'
        },
        text: `Slide ${index}`,
        children: [],
        parentId: maskId,
        visibility: 'visible',
        isContainer: true
      });

      // Create arrow as link with image child
      const createArrow = (id, direction) => {
        // Create unique IDs for the image child
        const imageId = id + 1000; // Add 1000 to avoid ID collisions

        // Arrow link element
        return {
          id,
          builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          type: 'link',
          sliderRole: 'arrow',
          direction: direction,
          text: '',
          styles: {
            position: 'absolute',
            bottom: '15px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '40px',
            height: '40px',
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            border: 'none',
            borderRadius: '50%',
            cursor: 'pointer',
            zIndex: 5,
            textDecoration: 'none',
            boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
            // Position left or right arrow accordingly
            left: direction === 'left' ? 'calc(50% - 45px)' : 'auto',
            right: direction === 'right' ? 'calc(50% - 45px)' : 'auto'
          },
          children: [imageId], // Add the image as a child
          parentId: sliderId,
          visibility: 'visible',
          hierarchyName: 'Arrow Link',
          interactions: {
            events: [
              {
                id: Date.now() + (direction === 'left' ? 1 : 2),
                type: 'click',
                action: 'custom',
                label: `${direction === 'left' ? 'Previous' : 'Next'} slide`,
                value: {
                  script: `
                    const slider = event.target.closest('[data-element-type="slider"]');
                    if (!slider) return;
                    
                    const mask = slider.querySelector('[data-slider-role="mask"]');
                    if (!mask) return;
                    
                    // Get device-specific settings based on current screen size
                    let slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 1);
                    let slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || 1);
                    
                    // Get loop setting
                    const loopEnabled = slider.getAttribute('data-loop') === 'true';
                    
                    // Choose breakpoint based on window width
                    const windowWidth = window.innerWidth;
                    let currentDevice = 'desktop';
                    
                    if (windowWidth <= 768) {
                      currentDevice = 'mobile';
                    } else if (windowWidth <= 1024) {
                      currentDevice = 'tablet';
                    }
                    
                    // Simpler approach - just use the values from the slider attributes
                    // (the Preview component will have already set the data attributes with the right values for the current device)
                    
                    const slides = mask.children.length;
                    let currentSlide = parseInt(mask.getAttribute('data-current-slide') || 0);
                    
                    if ('${direction}' === 'left') {
                      if (currentSlide === 0 && loopEnabled) {
                        // If at the beginning and loop is enabled, go to the end
                        currentSlide = slides - slidesPerView;
                      } else {
                        // Normal navigation with boundary check
                        currentSlide = Math.max(0, currentSlide - slidesPerScroll);
                      }
                    } else {
                      if (currentSlide >= slides - slidesPerView && loopEnabled) {
                        // If at the end and loop is enabled, go to the beginning
                        currentSlide = 0;
                      } else {
                        // Normal navigation with boundary check
                        currentSlide = Math.min(slides - slidesPerView, currentSlide + slidesPerScroll);
                      }
                    }
                    
                    // Apply transition for smooth movement
                    mask.style.transition = 'transform 0.5s ease-in-out';
                    mask.style.transform = 'translateX(-' + (currentSlide * (100/slidesPerView)) + '%)';
                    mask.setAttribute('data-current-slide', currentSlide);
                    
                    event.preventDefault(); // Prevent default link behavior
                  `
                }
              }
            ]
          },
          attributes: {
            href: '#',
            'aria-label': direction === 'left' ? 'Previous slide' : 'Next slide'
          }
        };
      };
      // Now we need to create the arrow image elements
      const createArrowImage = (id, parentId, direction) => ({
        id,
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'image',
        text: '',
        styles: {
          width: '20px',
          height: '20px',
          objectFit: 'contain',
        },
        src: direction === 'left'
          ? 'https://cdn-icons-png.flaticon.com/512/130/130882.png'  // Left arrow icon
          : 'https://cdn-icons-png.flaticon.com/512/130/130884.png', // Right arrow icon
        children: [],
        parentId: parentId,
        visibility: 'visible',
        hierarchyName: `${direction.charAt(0).toUpperCase() + direction.slice(1)} Arrow Icon`,
      });

      const slide1 = createSlide(slide1Id, 1);
      const slide2 = createSlide(slide2Id, 2);
      const slide3 = createSlide(slide3Id, 3);
      const leftArrow = createArrow(leftArrowId, 'left');
      const rightArrow = createArrow(rightArrowId, 'right');

      // Create the arrow image elements
      const leftArrowImage = createArrowImage(leftArrowId + 1000, leftArrowId, 'left');
      const rightArrowImage = createArrowImage(rightArrowId + 1000, rightArrowId, 'right');

      // Add draggable slider functionality
      const sliderDragScript = {
        id: Date.now() + 3,
        type: 'custom',
        action: 'custom',
        label: 'Initialize slider drag functionality',
        value: {
          script: `
            // Initialize drag functionality for this slider
            const mask = document.querySelector('[data-builder-id="${maskElement.builderId}"]');
            if (!mask) return;
            
            let isDragging = false;
            let startPosition = 0;
            let currentTranslate = 0;
            let prevTranslate = 0;
            
            mask.addEventListener('mousedown', dragStart);
            mask.addEventListener('touchstart', dragStart);
            mask.addEventListener('mouseup', dragEnd);
            mask.addEventListener('touchend', dragEnd);
            mask.addEventListener('mouseleave', dragEnd);
            mask.addEventListener('mousemove', drag);
            mask.addEventListener('touchmove', drag);
            
            function dragStart(e) {
              e.preventDefault();
              if (e.type === 'touchstart') {
                startPosition = e.touches[0].clientX;
              } else {
                startPosition = e.clientX;
              }
              isDragging = true;
              
              // Get current transform value
              const style = window.getComputedStyle(mask);
              const matrix = new WebKitCSSMatrix(style.transform);
              prevTranslate = matrix.m41; // Get the X translation
            }
            
            function drag(e) {
              if (!isDragging) return;
              
              let currentPosition;
              if (e.type === 'touchmove') {
                currentPosition = e.touches[0].clientX;
              } else {
                currentPosition = e.clientX;
              }
              
              const slider = mask.closest('[data-element-type="slider"]');
              if (!slider) return;
              
              const slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 1);
              const slides = mask.children.length;
              
              // Calculate drag distance
              const diff = currentPosition - startPosition;
              
              // Calculate what the new position would be
              currentTranslate = prevTranslate + diff;
              
              // Calculate the movement boundaries
              const maskWidth = mask.offsetWidth;
              const slideWidth = maskWidth / slidesPerView;
              const maxTranslate = 0;
              const minTranslate = -((slides - slidesPerView) * slideWidth);
              
              // Apply constraints
              if (currentTranslate > maxTranslate) {
                currentTranslate = maxTranslate;
              } else if (currentTranslate < minTranslate) {
                currentTranslate = minTranslate;
              }
              
              // Apply the transform
              mask.style.transform = \`translateX(\${currentTranslate}px)\`;
            }
            
            function dragEnd() {
              if (!isDragging) return;
              isDragging = false;
              
              const slider = mask.closest('[data-element-type="slider"]');
              if (!slider) return;
              
              const slidesPerView = parseInt(slider.getAttribute('data-slides-per-view') || 1);
              const slidesPerScroll = parseInt(slider.getAttribute('data-slides-per-scroll') || 1);
              const slides = mask.children.length;
              
              // Calculate the slide width
              const maskWidth = mask.offsetWidth;
              const slideWidth = maskWidth / slidesPerView;
              
              // Calculate the current slide based on position
              const currentSlide = Math.round(Math.abs(currentTranslate) / slideWidth);
              
              // Set to closest snap point
              const snapTranslate = -currentSlide * slideWidth;
              mask.style.transform = \`translateX(\${snapTranslate}px)\`;
              
              // Update data-current-slide attribute
              mask.setAttribute('data-current-slide', currentSlide);
            }
          `
        }
      };

      // Add drag functionality to slider element
      const updatedSliderElement = {
        ...sliderElement,
        interactions: {
          events: [sliderDragScript]
        }
      };

      setElements(prevElements => [
        ...prevElements,
        updatedSliderElement,
        maskElement,
        slide1,
        slide2,
        slide3,
        leftArrow,
        rightArrow,
        leftArrowImage,
        rightArrowImage
      ]);

      setSelectedElement(updatedSliderElement);
      return;
    }

    if (type === 'cart') {
      const { elements: cartElements } = createCartElements(
        builderId,
        getHierarchyName,
        currentPageId // Pass the currentPageId here
      );

      // Add all cart elements
      setElements(prevElements => [...prevElements, ...cartElements]);

      setSelectedElement(cartElements[0]);
      setNewlyAddedElement(cartElements[0]);

      // Return the first element (cart container)
      return cartElements[0];
    }

    if (type === 'add-to-cart') {
      // const addToCartId = uuidv4();

      const addToCartButton = {
        // id: addToCartId,
        id: Date.now(),
        builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        type: 'add-to-cart',
        name: 'Add to Cart Button',
        parentId: parentId,
        styles: {
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '10px 20px',
          backgroundColor: '#4CAF50',
          color: '#ffffff',
          border: 'none',
          borderRadius: '4px',
          fontSize: '16px',
          fontWeight: '500',
          cursor: 'pointer',
          transition: 'all 0.2s ease'
        },
        className: '',
        children: [],
        text: 'Add to Cart',
        attributes: {
          'data-element': 'add-to-cart',
          'data-action': 'add-to-cart',
          'data-product-id': 'product-123', // Default product ID, can be changed
          'data-product-name': 'Product', // Default product name
          'data-price': '29.99', // Default price, can be changed
          'data-image': '', // Will be populated from CMS if available
          'aria-label': 'Add item to cart',
          'type': 'button'
        }
      };

      setElements(prevElements => [...prevElements, addToCartButton]);
      setSelectedElement(addToCartButton);
      return;
    }

    const newElement = {
      id: `element-${Date.now()}`,
      builderId,
      type,
      hierarchyName: getHierarchyName(type),
      text: getDefaultText(type),
      className: '',
      // Initialize stateClassName
      stateClassName: {
        hover: '',
        pressed: '',
        focused: ''
      },
      // Initialize currentState
      currentState: 'default',
      styles: getDefaultStyles(type),
      children: [],
      parentId: validParent ? validParent.id : null,
      isRichText: type === 'rich-text',
      contentTypeId: null,
      fieldName: null,
      visibility: 'visible',
      isExpanded: true  // Add this property
    };

    setElements(prevElements => {
      let updatedElements = [];

      // Special handling for list creation
      if (type === "list") {
        const listItemIds = [];
        const listItems = Array(3).fill().map((_, index) => {
          const listItemId = Date.now() + index + 1;
          listItemIds.push(listItemId);
          return {
            id: listItemId,
            builderId: `builder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            type: "listItem",
            hierarchyName: `List Item ${index + 1}`,
            text: `List Item ${index + 1}`,
            styles: {
              display: "list-item",
              listStyleType: "disc",
              padding: "5px 0",
            },
            children: [],
            parentId: newElement.id,
            visibility: 'visible',
            isExpanded: true  // Add this property
          };
        });

        newElement.children = listItemIds;
        updatedElements = [...listItems];
      }

      // Special handling for rich-text elements
      if (type === "rich-text") {
        // Set initial content with proper HTML
        newElement.text = `<p>This is a rich text editor. You can add <strong>bold</strong>, <em>italic</em>, or <u>underlined</u> text.</p><p>You can also create lists:</p><ul><li>Item 1</li><li>Item 2</li><li>Item 3</li></ul><p>Click to edit!</p>`;
        // Mark as rich text
        newElement.isRichText = true;
      }

      if (validParent) {
        return prevElements
          .map(element => {
            if (element.id === validParent.id) {
              return {
                ...element,
                children: element.children
                  ? [...element.children, newElement.id]
                  : [newElement.id],
              };
            }
            return element;
          })
          .concat(newElement)
          .concat(updatedElements);
      } else {
        return [...prevElements, newElement, ...updatedElements];
      }
    });

    setSelectedElement(newElement);

    // Set this as the newly added element
    setNewlyAddedElement(newElement);

    // If this is a rich-text element, set the flag to show the editor
    if (type === 'rich-text') {
      setShowRichTextEditorForNewElement(true);
    }

    return newElement;
  };

  // Add this helper function to find the next available number for a class name
  const getNextClassNumber = (elementType) => {
    // Filter class names that match the pattern 'elementType-number'
    const pattern = new RegExp(`^${elementType}-\\d+$`);
    const existingNumbers = Array.from(globalClassNames)
      .filter(className => pattern.test(className))
      .map(className => parseInt(className.split('-')[1]))
      .sort((a, b) => a - b);

    // If no existing numbers, start with 1
    if (existingNumbers.length === 0) return 1;

    // Return the next number in sequence
    return existingNumbers[existingNumbers.length - 1] + 1;
  };

  // Update the updateElementStyles function
  const updateElementStyles = (id, propertyName, propertyValue, updatedStyles, device = selectedDevice) => {
    updateStyles({
      id,
      propertyName,
      propertyValue,
      updatedStyles,
      device,
      elements,
      selectedElement,
      setElements,
      setSelectedElement,
      classStylesRegistry,
      setClassStylesRegistry,
      combinedClassStylesRegistry,
      setCombinedClassStylesRegistry,
      currentPageId,
      setPages,
      getNextClassNumber,
      getDefaultStyles,
      addToGlobalClassNames,
      getStateStyles,
      getCascadedStyles,  // Make sure this matches your getCascadedStyles function name      
    });
  };

  const updateElementText = (id, updatedElement) => {
    setElements((prevElements) =>
      prevElements.map((element) =>
        element.id === id ? updatedElement : element
      )
    );
  };

  const addToGlobalClassNames = (className) => {
    // Only add if it's a non-empty string and replace spaces with dashes
    if (typeof className === 'string' && className.trim()) {
      const cleanClassName = className.trim().replace(/\s+/g, '-');
      setGlobalClassNames(prev => new Set([...prev, cleanClassName]));
    }
  };

  // Update the getCascadedStyles function
  const getCascadedStyles = (className, device) => {
    // Check if this is a combined class name (contains '.' or '+')
    const classNames = className.includes('.') ? className.split('.') : className.split('+');

    // If multiple classes
    if (classNames.length > 1) {
      // Convert to plus notation for registry lookup
      const registryKey = classNames.join('+');

      // Get combined styles for the current device
      const deviceStyles = combinedClassStylesRegistry[device]?.get(registryKey) || {};

      // Get styles from larger breakpoints if needed
      let cascadedStyles = {};

      switch (device) {
        case DEVICES.MOBILE:
          Object.assign(
            cascadedStyles,
            combinedClassStylesRegistry[DEVICES.DESKTOP]?.get(registryKey) || {},
            combinedClassStylesRegistry[DEVICES.TABLET]?.get(registryKey) || {},
            deviceStyles
          );
          break;
        case DEVICES.TABLET:
          Object.assign(
            cascadedStyles,
            combinedClassStylesRegistry[DEVICES.DESKTOP]?.get(registryKey) || {},
            deviceStyles
          );
          break;
        default:
          cascadedStyles = deviceStyles;
      }

      return cascadedStyles;
    } else {
      // Handle individual class styles (existing logic)
      const deviceStyles = classStylesRegistry[device]?.get(className) || {};

      let cascadedStyles = {};

      switch (device) {
        case DEVICES.MOBILE:
          Object.assign(
            cascadedStyles,
            classStylesRegistry[DEVICES.DESKTOP]?.get(className) || {},
            classStylesRegistry[DEVICES.TABLET]?.get(className) || {},
            deviceStyles
          );
          break;
        case DEVICES.TABLET:
          Object.assign(
            cascadedStyles,
            classStylesRegistry[DEVICES.DESKTOP]?.get(className) || {},
            deviceStyles
          );
          break;
        default:
          cascadedStyles = deviceStyles;
      }

      return cascadedStyles;
    }
  };

  // Update updateElementClassName to handle breakpoint-specific styles
  const updateElementClassName = (id, newClassName, state = null) => {
    // Keep the array order as is, don't sort
    const classNamesArray = Array.isArray(newClassName) ? newClassName : [newClassName];
    // Get the element
    const element = elements.find(el => el.id === id);
    if (!element) return;

    // Get existing classes and filter out any state classes for the current state
    const existingClasses = (element.className || '').split(' ').filter(c => !state || !c.includes(`--${state}`));

    // If adding state classes, create state-specific class names
    let updatedClasses;
    if (state) {
      // Create state-specific versions of the base classes
      const stateClasses = classNamesArray.map(className => `${className}--${state}`);
      // Combine existing classes with new state classes
      updatedClasses = [...existingClasses, ...stateClasses];
    } else {
      // If not a state update, just use the new class names
      updatedClasses = classNamesArray;
    }

    // Get cascaded styles for current device
    const combinedStyles = {};

    // Handle single vs multiple classes differently
    if (updatedClasses.length === 1) {
      // Single class - get styles directly from classStylesRegistry
      let cascadedStyles = getCascadedStyles(updatedClasses[0], selectedDevice);

      // If no styles found in registry and it's a new class, get default styles
      if (Object.keys(cascadedStyles).length === 0 && !existingClasses.includes(updatedClasses[0])) {
        cascadedStyles = getDefaultStyles(element.type.toLowerCase(), element);
      }

      Object.assign(combinedStyles, cascadedStyles);

      // If in a state, add state-specific styles
      if (state) {
        const stateStyles = getStateStyles(updatedClasses[0], state, selectedDevice);
        Object.assign(combinedStyles, stateStyles);
      }
    } else if (updatedClasses.length > 1) {
      // Multiple classes - use combinedClassStylesRegistry
      const combinationKey = updatedClasses.join('+');

      // Get cascaded combined styles from all breakpoints using the same logic
      const getCascadedCombinedStyles = (key, device) => {
        let styles = {};

        switch (device) {
          case DEVICES.MOBILE:
            styles = {
              ...combinedClassStylesRegistry[DEVICES.DESKTOP]?.get(key) || {},
              ...combinedClassStylesRegistry[DEVICES.TABLET]?.get(key) || {},
              ...combinedClassStylesRegistry[DEVICES.MOBILE]?.get(key) || {}
            };
            break;
          case DEVICES.TABLET:
            styles = {
              ...combinedClassStylesRegistry[DEVICES.DESKTOP]?.get(key) || {},
              ...combinedClassStylesRegistry[DEVICES.TABLET]?.get(key) || {}
            };
            break;
          default:
            styles = combinedClassStylesRegistry[device]?.get(key) || {};
        }

        return styles;
      };

      // Get cascaded styles for the combination
      let cascadedCombinedStyles = getCascadedCombinedStyles(combinationKey, selectedDevice);

      // If no combined styles found, try to get styles from existing combination or first class
      if (Object.keys(cascadedCombinedStyles).length === 0) {
        if (updatedClasses.length > 2) {
          // Try getting styles from existing combination (without the new class)
          const existingCombinationKey = updatedClasses.slice(0, -1).join('+');
          cascadedCombinedStyles = getCascadedCombinedStyles(existingCombinationKey, selectedDevice);
        }

        // If still no styles, fallback to first class styles
        if (Object.keys(cascadedCombinedStyles).length === 0) {
          cascadedCombinedStyles = getCascadedStyles(updatedClasses[0], selectedDevice);
        }
      }

      Object.assign(combinedStyles, cascadedCombinedStyles);

      // If in a state, get state-specific combined styles
      if (state) {
        const stateKey = `${combinationKey}--${state}`;
        const stateCombinedStyles = getCascadedCombinedStyles(stateKey, selectedDevice);
        Object.assign(combinedStyles, stateCombinedStyles);
      }
    }

    // Update elements
    setElements(prevElements =>
      prevElements.map(element => {
        if (element.id === id) {
          return {
            ...element,
            className: updatedClasses.join(' '),
            styles: combinedStyles
          };
        }
        return element;
      })
    );

    // Update selected element if needed
    if (selectedElement && selectedElement.id === id) {
      setSelectedElement(prev => ({
        ...prev,
        className: updatedClasses.join(' '),
        styles: combinedStyles
      }));
    }
  };

  const toggleSidebar = (sidebarType) => {
    // Close all other sidebars when opening a new one
    if (sidebarType === "element") {
      setShowHierarchySidebar(false);
      setShowAssetsSidebar(false);
      setShowPagesSidebar(false);
      setShowClientComponentsSidebar(false);
      setShowElementSidebar(!showElementSidebar);
    } else if (sidebarType === "hierarchy") {
      setShowElementSidebar(false);
      setShowAssetsSidebar(false);
      setShowPagesSidebar(false);
      setShowClientComponentsSidebar(false);
      setShowHierarchySidebar(!showHierarchySidebar);
    } else if (sidebarType === "assets") {
      setShowElementSidebar(false);
      setShowHierarchySidebar(false);
      setShowPagesSidebar(false);
      setShowClientComponentsSidebar(false);
      setShowAssetsSidebar(!showAssetsSidebar);
    } else if (sidebarType === "pages") {
      setShowElementSidebar(false);
      setShowHierarchySidebar(false);
      setShowAssetsSidebar(false);
      setShowClientComponentsSidebar(false);
      setShowPagesSidebar(!showPagesSidebar);
    } else if (sidebarType === "client-components") {
      setShowElementSidebar(false);
      setShowHierarchySidebar(false);
      setShowAssetsSidebar(false);
      setShowPagesSidebar(false);
      setShowClientComponentsSidebar(!showClientComponentsSidebar);
    }
  };

  const toggleCodeView = () => {
    setSidebarView(sidebarView === "code" ? null : "code");
  };

  const toggleAssetsSidebar = () => {
    if (showElementSidebar) setShowElementSidebar(false);
    if (showHierarchySidebar) setShowHierarchySidebar(false);
    setShowAssetsSidebar(!showAssetsSidebar);
  };

  const toggleSectionsSidebar = () => {
    setShowSectionsSidebar(!showSectionsSidebar);
    setSelectedSectionCategory(null);
  };

  const toggleClientComponentsSidebar = () => {
    setShowClientComponentsSidebar(!showClientComponentsSidebar);
  };

  const handleAssetSelect = (asset) => {
    setSelectedAssetFile(asset);
    setShowAssetsSidebar(false);
  };

  const handleDeviceSelect = (device, skipStateReset = false) => {
    // Only reset state if not skipped
    if (!skipStateReset && selectedElement) {
      // First reset the state in the element
      setSelectedElement(prev => ({
        ...prev,
        currentState: 'default'
      }));

      // Then update the element state in the registry
      updateElementState(selectedElement.id, selectedElement.currentState, false);
    }

    setSelectedDevice(device);
    setHasManualResize(false);
  };

  // Function to save state
  const saveState = async () => {
    if (!siteId) return; // Don't save if no siteId

    // Get user data
    const user = localStorage.getItem('user');
    if (!user) return;
    const userData = JSON.parse(user);

    try {
      // Prepare the editor data to save
      console.log("currentPageId in Save State:", currentPageId);

      const editorData = {
        pages: pages.map(page => {
          const isCurrentPage = page.id === currentPageId;

          return {
            ...page,
            elements: isCurrentPage ? elements : page.elements,
            classStylesRegistry: {
              [DEVICES.DESKTOP]: Object.fromEntries(
                (isCurrentPage ? classStylesRegistry[DEVICES.DESKTOP] : page.classStylesRegistry[DEVICES.DESKTOP]) || new Map()
              ),
              [DEVICES.TABLET]: Object.fromEntries(
                (isCurrentPage ? classStylesRegistry[DEVICES.TABLET] : page.classStylesRegistry[DEVICES.TABLET]) || new Map()
              ),
              [DEVICES.MOBILE]: Object.fromEntries(
                (isCurrentPage ? classStylesRegistry[DEVICES.MOBILE] : page.classStylesRegistry[DEVICES.MOBILE]) || new Map()
              ),
            },
            combinedClassStylesRegistry: {
              [DEVICES.DESKTOP]: Object.fromEntries(
                (isCurrentPage ? combinedClassStylesRegistry[DEVICES.DESKTOP] : page.combinedClassStylesRegistry[DEVICES.DESKTOP]) || new Map()
              ),
              [DEVICES.TABLET]: Object.fromEntries(
                (isCurrentPage ? combinedClassStylesRegistry[DEVICES.TABLET] : page.combinedClassStylesRegistry[DEVICES.TABLET]) || new Map()
              ),
              [DEVICES.MOBILE]: Object.fromEntries(
                (isCurrentPage ? combinedClassStylesRegistry[DEVICES.MOBILE] : page.combinedClassStylesRegistry[DEVICES.MOBILE]) || new Map()
              ),
            },
          };
        }),
        currentPageId: 'home',
        globalClassNames: Array.from(globalClassNames),
        elementInteractions,
        // Add any other state you want to save
      };

      console.log("classStylesRegistry in Save State:", classStylesRegistry);
      console.log("combinedClassStylesRegistry in Save State:", combinedClassStylesRegistry);
      console.log("editorData in Save State:", editorData);

      // Send editor data to the server
      const response = await fetch(buildApiUrl(`api/sites/${siteId}/editor-data`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          editorData,
          userId: userData.id
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.error('Error saving editor data:', data.error);
        // Show error notification
        // showNotification(`Failed to save: ${data.error}`, "error");
      } else {
        // Show success notification
        // showNotification("Site saved successfully", "success");
        console.log('Site saved successfully');
      }
    } catch (error) {
      console.error('Error saving state:', error);
      // showNotification(`Error saving: ${error.message}`, "error");
    }
  };

  // Add autosave functionality
  useEffect(() => {
    if (!siteId) return;

    // Setup autosave interval
    const autosaveInterval = setInterval(() => {
      // saveState();
    }, 60000); // Autosave every minute

    return () => {
      // clearInterval(autosaveInterval);
    };
  }, [elements, pages, currentPageId, siteId]); // Dependencies to trigger autosave

  // Add useEffect to load state on mount
  useEffect(() => {
    // loadState();
  }, []); // Empty dependency array means this runs once on mount

  // Update the loadState function
  /*const loadState = async () => {
    const savedState = localStorage.getItem('builderState');
    if (savedState) {
      try {
        const parsedState = JSON.parse(savedState);
        
        // Convert stored registry arrays back to Maps for each page
        const pagesWithMaps = parsedState.pages.map(page => ({
          ...page,
          classStylesRegistry: {
            [DEVICES.DESKTOP]: new Map(page.classStylesRegistry[DEVICES.DESKTOP] || []),
            [DEVICES.TABLET]: new Map(page.classStylesRegistry[DEVICES.TABLET] || []),
            [DEVICES.MOBILE]: new Map(page.classStylesRegistry[DEVICES.MOBILE] || [])
          },
          combinedClassStylesRegistry: {
            [DEVICES.DESKTOP]: new Map(page.combinedClassStylesRegistry[DEVICES.DESKTOP] || []),
            [DEVICES.TABLET]: new Map(page.combinedClassStylesRegistry[DEVICES.TABLET] || []),
            [DEVICES.MOBILE]: new Map(page.combinedClassStylesRegistry[DEVICES.MOBILE] || [])
          }
        }));

        // Set pages state
        setPages(pagesWithMaps);

        // Set current page ID
        const loadedCurrentPageId = parsedState.currentPageId || 'home';
        setCurrentPageId(loadedCurrentPageId);

        // Find and load current page data
        const currentPage = pagesWithMaps.find(page => page.id === loadedCurrentPageId);
        if (currentPage) {
          setElements(currentPage.elements || []);
          setClassStylesRegistry(currentPage.classStylesRegistry);
          setCombinedClassStylesRegistry(currentPage.combinedClassStylesRegistry);
        }

        // Load other global states
        setGlobalClassNames(new Set(parsedState.globalClassNames || []));
        setElementInteractions(parsedState.elementInteractions || {});

      } catch (error) {
        console.error('Error loading saved state:', error);
        
        // Set default state if loading fails
        const defaultPage = {
          id: 'home',
          name: 'Home',
          isHome: true,
          elements: [],
          classStylesRegistry: {
            [DEVICES.DESKTOP]: new Map(),
            [DEVICES.TABLET]: new Map(),
            [DEVICES.MOBILE]: new Map()
          },
          combinedClassStylesRegistry: {
            [DEVICES.DESKTOP]: new Map(),
            [DEVICES.TABLET]: new Map(),
            [DEVICES.MOBILE]: new Map()
          }
        };

        setPages([defaultPage]);
        setCurrentPageId('home');
        setElements([]);
        setClassStylesRegistry({
          [DEVICES.DESKTOP]: new Map(),
          [DEVICES.TABLET]: new Map(),
          [DEVICES.MOBILE]: new Map()
        });
        setCombinedClassStylesRegistry({
          [DEVICES.DESKTOP]: new Map(),
          [DEVICES.TABLET]: new Map(),
          [DEVICES.MOBILE]: new Map()
        });
      }
    }
  }; */

  const toggleLivePreview = () => {
    setShowLivePreview(!showLivePreview);
  };

  const updateElementInteractions = (elementId, eventType, value) => {
    setElementInteractions(prev => ({
      ...prev,
      [elementId]: {
        ...prev[elementId],
        [eventType]: value
      }
    }));

    // Update elements to include interaction data
    setElements(prevElements =>
      prevElements.map(element => {
        if (element.id === elementId) {
          return {
            ...element,
            interactions: {
              ...element.interactions,
              [eventType]: value
            }
          };
        }
        return element;
      })
    );
  };

  const debugLocalStorage = () => {
    // Note: Published site data is no longer stored in localStorage to prevent quota issues
    // Site data is now only stored on the backend
    console.log('Published site data is stored on the backend, not in localStorage');
  };

  const handlePublish = async (publishData = {}) => {
    try {
      const siteData = {
        pages: {}
      };

      console.log('pages in handlePublish:', pages);

      // Process each page
      for (const [pageId, page] of Object.entries(pages)) {
        const buildFiles = await generatePublishedBuild(page, pages, siteFonts);

        if (buildFiles.isTemplate && buildFiles.pages) {
          // For template pages, merge the generated pages into siteData.pages
          Object.assign(siteData.pages, buildFiles.pages);
        } else {
          // For regular pages
          siteData.pages[pageId] = {
            id: page.id,
            isTemplatePage: false,
            name: page.name,
            isHome: page.isHome,
            slug: page.slug || page.name,
            buildFiles
          };
        }
      }

      // Prepare request body with publish options
      const requestBody = {
        siteId: window.location.pathname.split('/editor/')[1],
        siteData,
        deployToCloudflare: publishData.deployToCloudflare || false,
        environment: publishData.environment || 'production',
        subdomain: publishData.subdomain || null,
        useCustomDomain: publishData.useCustomDomain || false,
        customDomain: publishData.customDomain || null
      };

      console.log('Publishing with data:', requestBody);

      // Send to backend
      const response = await fetch(buildApiUrl('api/sites/publish'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        console.log('Site published successfully:', data);
        // Note: Removed localStorage storage to prevent quota exceeded errors
        // The site data is already stored on the backend via the API call

        return {
          success: true,
          url: data.url,
          previewUrl: data.previewUrl,
          cloudflareUrl: data.cloudflareUrl,
          deployment: data.deployment
        };
      } else {
        throw new Error(data.error || 'Failed to publish site');
      }
    } catch (error) {
      console.error('Error publishing site:', error);
      throw error;
    }
  };

  const handleAddPage = (pageName, initialElements = []) => {
    const newPageId = `page-${Date.now()}`;
    const newPage = {
      id: newPageId,
      name: pageName,
      isHome: false,
      elements: initialElements,
      classStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      },
      combinedClassStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      },
      customCode: {
        head: '',
        body: ''
      }
    };
    setPages(prevPages => [...prevPages, newPage]);
    return newPageId;
  };

  const handlePageChange = (pageId) => {
    // Save current page state
    setPages(prevPages =>
      prevPages.map(page =>
        page.id === currentPageId
          ? {
            ...page,
            elements: elements,
            classStylesRegistry: {
              [DEVICES.DESKTOP]: new Map(classStylesRegistry[DEVICES.DESKTOP] || []),
              [DEVICES.TABLET]: new Map(classStylesRegistry[DEVICES.TABLET] || []),
              [DEVICES.MOBILE]: new Map(classStylesRegistry[DEVICES.MOBILE] || [])
            },
            combinedClassStylesRegistry: {
              [DEVICES.DESKTOP]: new Map(combinedClassStylesRegistry[DEVICES.DESKTOP] || []),
              [DEVICES.TABLET]: new Map(combinedClassStylesRegistry[DEVICES.TABLET] || []),
              [DEVICES.MOBILE]: new Map(combinedClassStylesRegistry[DEVICES.MOBILE] || [])
            }
          }
          : page
      )
    );

    // Load new page state
    const newPage = pages.find(page => page.id === pageId);
    if (newPage) {
      console.log('newPage:', newPage);
      // Load the page-specific elements
      const pageElements = newPage.elements || [];
      setElements(pageElements);

      setClassStylesRegistry({
        [DEVICES.DESKTOP]: new Map(newPage.classStylesRegistry?.[DEVICES.DESKTOP] || []),
        [DEVICES.TABLET]: new Map(newPage.classStylesRegistry?.[DEVICES.TABLET] || []),
        [DEVICES.MOBILE]: new Map(newPage.classStylesRegistry?.[DEVICES.MOBILE] || [])
      });
      setCombinedClassStylesRegistry({
        [DEVICES.DESKTOP]: new Map(newPage.combinedClassStylesRegistry?.[DEVICES.DESKTOP] || []),
        [DEVICES.TABLET]: new Map(newPage.combinedClassStylesRegistry?.[DEVICES.TABLET] || []),
        [DEVICES.MOBILE]: new Map(newPage.combinedClassStylesRegistry?.[DEVICES.MOBILE] || [])
      });
      setCurrentPageId(pageId);
      setSelectedElement(null);
    }
  };

  const handleRenamePage = (pageId, newName) => {
    setPages(prevPages =>
      prevPages.map(page =>
        page.id === pageId
          ? { ...page, name: newName }
          : page
      )
    );
  };

  const handleDeletePage = (pageId) => {
    const newPages = pages.filter(page => page.id !== pageId);
    setPages(newPages);

    if (currentPageId === pageId) {
      const firstPage = newPages[0];
      setCurrentPageId(firstPage.id);
      setElements(firstPage.elements);
      setSelectedElement(null);
    }
  };

  // Add this useEffect to sync classStylesRegistry with current page
  // useEffect(() => {
  //   setPages(prevPages =>
  //     prevPages.map(page =>
  //       page.id === currentPageId
  //         ? {
  //             ...page,
  //             classStylesRegistry: classStylesRegistry,
  //             combinedClassStylesRegistry: combinedClassStylesRegistry
  //           }
  //         : page
  //     )
  //   );
  // }, [classStylesRegistry, combinedClassStylesRegistry, currentPageId]);

  // Add the device change effect
  useEffect(() => {
    if (!elements.length || !selectedDevice) return;

    const applyDeviceStyles = () => {
      setElements(prevElements =>
        prevElements.map(element => {
          if (element.className) {
            const classNames = element.className.split(' ');
            const combinedStyles = {};

            // Apply individual class styles with cascading
            classNames.forEach(className => {
              const cascadedStyles = getCascadedStyles(className, selectedDevice);
              Object.assign(combinedStyles, cascadedStyles);
            });


            // Apply combined class styles if multiple classes
            if (classNames.length > 1) {
              // Use original order from element.className instead of sorting
              const combinationKey = classNames.join('.');
              const cascadedCombinedStyles = getCascadedStyles(combinationKey, selectedDevice);
              Object.assign(combinedStyles, cascadedCombinedStyles);
            }

            return {
              ...element,
              styles: combinedStyles
            };
          }
          return element;
        })
      );

      // Update selected element if any
      if (selectedElement?.className) {
        setSelectedElement(prev => {
          const classNames = prev.className.split(' ');
          const combinedStyles = {};

          classNames.forEach(className => {
            const cascadedStyles = getCascadedStyles(className, selectedDevice);
            Object.assign(combinedStyles, cascadedStyles);
          });

          if (classNames.length > 1) {
            // Use original order here as well
            const combinationKey = classNames.join('.');
            const cascadedCombinedStyles = getCascadedStyles(combinationKey, selectedDevice);
            Object.assign(combinedStyles, cascadedCombinedStyles);
          }

          return {
            ...prev,
            styles: combinedStyles
          };
        });
      }
    };

    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(applyDeviceStyles);
  }, [selectedDevice, elements.length]); // Add elements.length to dependencies

  // Add this function to create template page
  const createTemplatePage = async (contentType) => {
    const templatePage = {
      id: `template-${contentType._id}`,
      name: `${contentType.name} Template`,
      isTemplate: true,
      contentTypeId: contentType._id,
      elements: [],
      classStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      },
      combinedClassStylesRegistry: {
        [DEVICES.DESKTOP]: new Map(),
        [DEVICES.TABLET]: new Map(),
        [DEVICES.MOBILE]: new Map()
      },
      customCode: {
        head: '',
        body: ''
      }
    };

    // Update pages and get the new state
    let updatedPages;
    setPages(prevPages => {
      // Check if template page already exists
      const exists = prevPages.some(p => p.id === templatePage.id);
      if (!exists) {
        updatedPages = [...prevPages, templatePage];
        return updatedPages;
      }
      updatedPages = prevPages;
      return prevPages;
    });

    // Save the state using the updated pages
    if (!siteId) return; // Don't save if no siteId

    const user = localStorage.getItem('user');
    if (!user) return;
    const userData = JSON.parse(user);

    try {
      const editorData = {
        elements,
        pages: updatedPages.map(page => ({
          ...page,
          classStylesRegistry: {
            [DEVICES.DESKTOP]: Object.fromEntries(page.classStylesRegistry[DEVICES.DESKTOP] || new Map()),
            [DEVICES.TABLET]: Object.fromEntries(page.classStylesRegistry[DEVICES.TABLET] || new Map()),
            [DEVICES.MOBILE]: Object.fromEntries(page.classStylesRegistry[DEVICES.MOBILE] || new Map())
          },
          combinedClassStylesRegistry: {
            [DEVICES.DESKTOP]: Object.fromEntries(page.combinedClassStylesRegistry[DEVICES.DESKTOP] || new Map()),
            [DEVICES.TABLET]: Object.fromEntries(page.combinedClassStylesRegistry[DEVICES.TABLET] || new Map()),
            [DEVICES.MOBILE]: Object.fromEntries(page.combinedClassStylesRegistry[DEVICES.MOBILE] || new Map())
          }
        })),
        currentPageId,
        globalClassNames: Array.from(globalClassNames),
        classStylesRegistry: {
          [DEVICES.DESKTOP]: Object.fromEntries(classStylesRegistry[DEVICES.DESKTOP] || new Map()),
          [DEVICES.TABLET]: Object.fromEntries(classStylesRegistry[DEVICES.TABLET] || new Map()),
          [DEVICES.MOBILE]: Object.fromEntries(classStylesRegistry[DEVICES.MOBILE] || new Map())
        },
        combinedClassStylesRegistry: {
          [DEVICES.DESKTOP]: Object.fromEntries(combinedClassStylesRegistry[DEVICES.DESKTOP] || new Map()),
          [DEVICES.TABLET]: Object.fromEntries(combinedClassStylesRegistry[DEVICES.TABLET] || new Map()),
          [DEVICES.MOBILE]: Object.fromEntries(combinedClassStylesRegistry[DEVICES.MOBILE] || new Map())
        },
        elementInteractions
      };

      const response = await fetch(buildApiUrl(`api/sites/${siteId}/editor-data`), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          editorData,
          userId: userData.id
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        console.error('Error saving editor data:', data.error);
      } else {
        console.log('Template page saved successfully');
      }
    } catch (error) {
      console.error('Error saving template page:', error);
    }
  };

  // Add effect to fetch content items when template page is selected
  useEffect(() => {
    const currentPage = pages.find(p => p.id === currentPageId);
    if (currentPage?.isTemplate) {
      fetchContentItems(currentPage.contentTypeId);
    }
  }, [currentPageId]);

  const fetchContentItems = async (contentTypeId) => {
    try {
      const siteId = window.location.pathname.split('/editor/')[1];
      const response = await fetch(
        buildApiUrl(`api/cms/content/${siteId}/${contentTypeId}`)
      );
      const data = await response.json();

      if (data.success) {
        setContentItems(data.content);
        if (data.content.length > 0) {
          setSelectedContentItem(data.content[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching content items:', error);
      showNotification('Failed to fetch content items', 'error');
    }
  };

  // Add this function to update all dynamic elements
  const updateDynamicElements = (contentItem) => {
    // Make sure we're setting the selectedContentItem state
    setSelectedContentItem(contentItem);

    if (!contentItem) return;

    // Rest of the function to update dynamic elements
    const currentElements = [...elements];
    const updatedElements = [];

    // Find all dynamic elements
    currentElements.forEach(element => {
      let needsUpdate = false;
      let updatedElement = { ...element };

      // Handle dynamic text fields
      if (element.dynamicField && contentItem.data) {
        const fieldValue = contentItem.data[element.dynamicField];

        // Check if the field content exists in the content item
        if (fieldValue !== undefined) {
          // Check if this is a rich text element or has rich text content
          const isRichTextElement = element.type === 'rich-text';
          const hasRichTextContent = element.isRichTextContent;

          // Update the element's text with the content item's data
          updatedElement.text = fieldValue || '';

          // Ensure rich text properties are preserved
          if (isRichTextElement || hasRichTextContent) {
            updatedElement.isRichText = true;
          }

          needsUpdate = true;
        }
      }

      // Handle dynamic image fields
      if (element.type === 'image' && element.dynamicImageField && contentItem.data) {
        const imageUrl = contentItem.data[element.dynamicImageField];

        if (imageUrl) {
          updatedElement.styles = {
            ...updatedElement.styles,
            backgroundImage: `url(${imageUrl})`
          };
          needsUpdate = true;
        }
      }

      // Only add to updated elements if changes were made
      if (needsUpdate) {
        updatedElements.push(updatedElement);
      }
    });

    // Update the elements with the new values
    if (updatedElements.length > 0) {
      const newElements = currentElements.map(element => {
        const updatedElement = updatedElements.find(e => e.id === element.id);
        return updatedElement || element;
      });

      setElements(newElements);

      // Update selected element if it was one of the changed ones
      if (selectedElement) {
        const updatedSelectedElement = updatedElements.find(el => el.id === selectedElement.id);
        if (updatedSelectedElement) {
          setSelectedElement(updatedSelectedElement);
        }
      }
    }
  };

  // Add the effect to watch for content item changes
  useEffect(() => {
    if (selectedContentItem) {
      updateDynamicElements(selectedContentItem);
    }
  }, [selectedContentItem]);

  // Update the deleteTemplatePage function
  const deleteTemplatePage = (contentTypeId) => {
    // Check if we're currently on the template page that's being deleted
    const templatePageId = `template-${contentTypeId}`;
    if (currentPageId === templatePageId) {
      // Navigate to home page using existing handlePageChange function
      handlePageChange('home');
    }

    // Remove the template page
    setPages(prevPages =>
      prevPages.filter(page => page.id !== templatePageId)
    );
  };

  const handleLiveBuild = () => {
    const updatedPages = pages.map(page =>
      page.id === currentPageId
        ? {
          ...page,
          elements: elements.map(el => ({
            ...el,
            contentTypeId: el.contentTypeId,
            displayType: el.displayType,
            limit: el.limit,
            sortBy: el.sortBy,
            sortOrder: el.sortOrder
          })),
          classStylesRegistry: {
            desktop: new Map(classStylesRegistry.desktop),
            tablet: new Map(classStylesRegistry.tablet),
            mobile: new Map(classStylesRegistry.mobile)
          }
        }
        : page
    );

    // console.log('Styles being passed to LiveBuild:', {
    //   desktop: Object.fromEntries(classStylesRegistry.desktop),
    //   tablet: Object.fromEntries(classStylesRegistry.tablet),
    //   mobile: Object.fromEntries(classStylesRegistry.mobile)
    // });

    setPages(updatedPages);
    setShowLiveBuild(true);
    // setShowCodeView(false);
  };

  // Add function to fetch all CMS data
  const fetchAllCMSData = async (specificTypeId = null) => {
    try {
      // Get siteId from URL
      const siteId = window.location.pathname.split('/editor/')[1];

      // Fetch content types
      const typesResponse = await fetch(buildApiUrl(`api/cms/types/${siteId}`));
      const typesData = await typesResponse.json();

      if (typesData.success) {
        setCmsContentTypes(typesData.contentTypes);

        // If we're only refreshing a specific content type
        if (specificTypeId) {
          const contentResponse = await fetch(
            buildApiUrl(`api/cms/content/${siteId}/${specificTypeId}`)
          );
          const contentData = await contentResponse.json();

          if (contentData.success) {
            setCmsContent(prev => ({
              ...prev,
              [specificTypeId]: contentData.content
            }));
          }
          return;
        }

        // Otherwise fetch content for all content types
        const contentPromises = typesData.contentTypes.map(async (type) => {
          const contentResponse = await fetch(
            buildApiUrl(`api/cms/content/${siteId}/${type._id}`)
          );
          const contentData = await contentResponse.json();
          return {
            typeId: type._id,
            content: contentData.success ? contentData.content : []
          };
        });

        const allContent = await Promise.all(contentPromises);
        const contentByType = allContent.reduce((acc, { typeId, content }) => {
          acc[typeId] = content;
          return acc;
        }, {});

        setCmsContent(contentByType);
      }
    } catch (error) {
      console.error('Error fetching CMS data:', error);
      showNotification('Failed to fetch CMS data', 'error');
    }
  };

  // Add useEffect to fetch CMS data on mount
  useEffect(() => {
    fetchAllCMSData();
  }, []);

  // Add function to update CMS content after changes
  const handleCMSContentUpdate = async (contentTypeId, immediateContent = null) => {
    // If immediate content is provided, update the state right away
    if (immediateContent) {
      setCmsContent(immediateContent);
      return;
    }

    try {
      const siteId = window.location.pathname.split('/editor/')[1];
      const response = await fetch(
        buildApiUrl(`api/cms/content/${siteId}/${contentTypeId}`)
      );
      const data = await response.json();

      if (data.success) {
        setCmsContent(prev => ({
          ...prev,
          [contentTypeId]: data.content
        }));
      }
    } catch (error) {
      console.error('Error updating CMS content:', error);
      showNotification('Failed to update CMS content', 'error');
    }
  };

  // Add function to handle content type creation
  const handleContentTypeCreate = async (contentType) => {
    try {
      // Create template page for this content type
      await createTemplatePage(contentType);

      // Only refresh data for this specific content type
      if (contentType._id) {
        await fetchAllCMSData(contentType._id);
      } else {
        // If it's a new content type, refresh all data
        await fetchAllCMSData();
      }
    } catch (error) {
      console.error('Error creating content type:', error);
      showNotification('Failed to create content type', 'error');
    }
  };

  // Add function to handle content type deletion
  const handleContentTypeDelete = async (contentTypeId) => {
    deleteTemplatePage(contentTypeId);
    await fetchAllCMSData(); // Refresh all CMS data
  };

  const updateSelectedElement = (element) => {
    if (element) {
      // Get the full element with all properties from elements array
      const fullElement = elements.find(e => e.id === element.id);
      if (fullElement) {
        setSelectedElement({
          ...fullElement,  // Keep all existing properties
          ...element,      // Add any new properties
          interactions: fullElement.interactions || {} // Ensure interactions are included
        });
      } else {
        // If element not found in elements array, use provided element
        setSelectedElement({
          ...element,
          interactions: element.interactions || {}
        });
      }
    } else {
      setSelectedElement(null);
    }
  };

  // Add this helper function to get state-specific styles
  const getStateStyles = (className, state, device) => {
    // Check if this is a combined class name (contains '+')
    const isMultipleClasses = className.includes('+');

    // Get the appropriate registry based on class type
    const registry = isMultipleClasses ? combinedClassStylesRegistry : classStylesRegistry;

    if (!className || state === 'default') {
      // console.log("Getting default styles for:"+className, registry[device]?.get(className));
      return registry[device]?.get(className) || {};
    }

    // Get state-specific class name
    const stateClassName = `${className}--${state}`;
    return registry[device]?.get(stateClassName) || {};
  };

  // Update updateElementState to handle styles
  const updateElementState = (elementId, state, enabled) => {
    const element = elements.find(el => el.id === elementId);
    if (!element) return;

    // Get base classes, handle empty className
    const baseClasses = (element.className || '').split(' ').filter(Boolean);

    if (baseClasses.length === 0) return; // No classes to update
    // Handle state classes differently based on number of classes
    let stateClasses = '';
    let baseClassName = '';

    if (baseClasses.length === 1) {
      // Single class case
      baseClassName = baseClasses[0];
      stateClasses = `${baseClassName}--${state}`;
    } else {
      // Multiple classes case
      baseClassName = baseClasses.join('+');
      stateClasses = `${baseClassName}--${state}`;
    }

    // Get styles based on state
    let combinedStyles = {};

    // console.log("baseClasses:", baseClasses);
    // Get cascaded styles based on number of classes
    if (baseClasses.length === 1) {
      // Single class case
      combinedStyles = getCascadedStyles(baseClassName, selectedDevice);
    } else {
      // Multiple classes case
      combinedStyles = getCascadedStyles(baseClassName, selectedDevice);
    }

    // If enabling a state, overlay state-specific styles
    if (enabled) {
      const stateStyles = getStateStyles(baseClassName, state, selectedDevice);
      combinedStyles = {
        ...combinedStyles,
        ...stateStyles
      };
    }

    // Update elements first
    setElements(prevElements =>
      prevElements.map(el => {
        if (el.id === elementId) {
          // Always preserve the stateClassName object
          const newStateClassName = {
            ...el.stateClassName || {}, // Keep existing states
            [state]: enabled ? stateClasses : '' // Keep existing state value if going to default
          };

          return {
            ...el,
            currentState: enabled ? state : 'default',
            stateClassName: newStateClassName,
            styles: combinedStyles,
            forceState: enabled ? state : null // Add this to track forced state
          };
        }
        return el;
      })
    );

    // Then update selected element
    if (selectedElement && selectedElement.id === elementId) {
      const newStateClassName = {
        ...selectedElement.stateClassName || {},
        [state]: enabled ? stateClasses : ''
      };

      setSelectedElement({
        ...selectedElement,
        currentState: enabled ? state : 'default',
        stateClassName: newStateClassName,
        styles: combinedStyles,
        forceState: enabled ? state : null // Add this to track forced state
      });
    }

    // Apply data attribute to DOM element for state visualization
    setTimeout(() => {
      try {
        const domElement = document.querySelector(`[data-builder-id="${element.builderId}"]`);
        if (domElement) {
          if (enabled) {
            domElement.setAttribute('data-force-state', state);
          } else {
            domElement.removeAttribute('data-force-state');
          }
        }
      } catch (error) {
        console.error("Error applying force state:", error);
      }
    }, 0);

    console.log("state in updateElementState:", state);
    console.log("combinedStyles in updateElementState:", combinedStyles);

    // If returning to default state, trigger device select to refresh styles
    if (!enabled) {
      handleDeviceSelect(selectedDevice, true);
    }
  };

  // In your App.js where you manage the pages state
  const handleUpdatePage = (pageId, settings) => {
    console.log('Updating page with settings:', settings); // Debug log
    setPages(pages.map(page => {
      if (page.id === pageId) {
        return {
          ...page,
          name: settings.name,
          slug: settings.slug,
          seo: settings.seo,
          customCode: settings.customCode || { head: '', body: '' }, // Add customCode property
          // Add template handling for template pages
          ...(page.isTemplate && {
            template: settings.template,
            contentTypeId: settings.contentTypeId
          })
        };
      }
      return page;
    }));
  };

  const loadSiteData = async (id) => {
    setLoading(true);
    try {
      // First check if the site exists
      const response = await fetch(buildApiUrl(`api/sites/${id}`));
      const data = await response.json();

      if (!response.ok) {
        console.error('Failed to load site:', data.error);
        alert(`Error loading site: ${data.error || 'Unknown error'}`);
        navigate('/dashboard');
        return;
      }

      // Fetch and load site fonts
      try {
        const fontsResponse = await fetch(buildApiUrl(`api/sites/${id}/fonts`));
        const fontsData = await fontsResponse.json();

        if (fontsResponse.ok && fontsData.success && fontsData.fonts) {
          // Load the fonts into the document head
          console.log("fontsData:", fontsData);
          loadSiteFonts(fontsData.fonts);
        }
      } catch (fontsError) {
        console.error('Error loading site fonts:', fontsError);
      }

      try {
        // Now fetch the editor data
        const editorResponse = await fetch(buildApiUrl(`api/sites/${id}/editor-data`));
        const editorData = await editorResponse.json();

        // If editor data exists and is valid, use it
        if (editorResponse.ok && editorData.success && editorData.editorData && Object.keys(editorData.editorData).length > 0) {
          // Convert stored registry objects back to Maps for each page
          const pagesWithMaps = editorData.editorData.pages?.map(page => ({
            ...page,
            classStylesRegistry: {
              [DEVICES.DESKTOP]: new Map(Object.entries(page.classStylesRegistry?.[DEVICES.DESKTOP] || {})),
              [DEVICES.TABLET]: new Map(Object.entries(page.classStylesRegistry?.[DEVICES.TABLET] || {})),
              [DEVICES.MOBILE]: new Map(Object.entries(page.classStylesRegistry?.[DEVICES.MOBILE] || {}))
            },
            combinedClassStylesRegistry: {
              [DEVICES.DESKTOP]: new Map(Object.entries(page.combinedClassStylesRegistry?.[DEVICES.DESKTOP] || {})),
              [DEVICES.TABLET]: new Map(Object.entries(page.combinedClassStylesRegistry?.[DEVICES.TABLET] || {})),
              [DEVICES.MOBILE]: new Map(Object.entries(page.combinedClassStylesRegistry?.[DEVICES.MOBILE] || {}))
            }
          })) || [];

          // Set all the state using existing state setters
          const homepageElements = editorData.editorData.pages.find(page => page.isHome)?.elements || [];
          setElements(homepageElements);
          setPages(pagesWithMaps);
          setCurrentPageId(editorData.editorData.currentPageId || 'home');
          setGlobalClassNames(new Set(editorData.editorData.globalClassNames || []));
          //Find Homepage
          const homepage = pagesWithMaps.find(page => page.isHome);
          if (homepage) {
            setClassStylesRegistry(homepage.classStylesRegistry);
            setCombinedClassStylesRegistry(homepage.combinedClassStylesRegistry);
          }

          setElementInteractions(editorData.editorData.elementInteractions || {});
          showNotification('Editor state loaded successfully', 'success');
        } else {
          // Initialize with default state for new sites
          console.log('No editor data found, initializing with defaults');
          initializeDefaultEditor();

          // Save the default state to the backend
          const defaultEditorData = {
            elements: [],
            pages: [{
              id: 'home',
              name: 'Home',
              isHome: true,
              elements: [],
              classStylesRegistry: {
                [DEVICES.DESKTOP]: {},
                [DEVICES.TABLET]: {},
                [DEVICES.MOBILE]: {}
              },
              combinedClassStylesRegistry: {
                [DEVICES.DESKTOP]: {},
                [DEVICES.TABLET]: {},
                [DEVICES.MOBILE]: {}
              },
              customCode: {
                head: '',
                body: ''
              }
            }],
            currentPageId: 'home',
            globalClassNames: [],
            classStylesRegistry: {
              [DEVICES.DESKTOP]: {},
              [DEVICES.TABLET]: {},
              [DEVICES.MOBILE]: {}
            },
            combinedClassStylesRegistry: {
              [DEVICES.DESKTOP]: {},
              [DEVICES.TABLET]: {},
              [DEVICES.MOBILE]: {}
            },
            elementInteractions: {}
          };

          // Save the default state to the backend
          await fetch(buildApiUrl(`api/sites/${id}/editor-data`), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              editorData: defaultEditorData,
              userId: data.site.user // Use the user ID from the site data
            }),
          });
        }
      } catch (editorError) {
        console.error('Error loading editor data:', editorError);
        initializeDefaultEditor();
      }
    } catch (error) {
      console.error('Error loading site data:', error);
      alert('Failed to load site data. Please try again.');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  // Reset rich text editor flag when selected element changes
  useEffect(() => {
    // Only reset the flag if it was true and we've selected an element
    if (showRichTextEditorForNewElement && selectedElement) {
      // We need to give a little time for the UI to update before resetting
      setTimeout(() => {
        setShowRichTextEditorForNewElement(false);
      }, 100);
    }
  }, [selectedElement, showRichTextEditorForNewElement]);

  // Add this new function to load fonts into the document head
  const loadSiteFonts = (siteFonts) => {
    // Remove any previously added custom font links
    removeCustomFonts('data-custom-font');

    // Add each font from the site
    loadFontsInHead(siteFonts, 'data-custom-font');

    // Save the fonts in state for the Inspector
    setSiteFonts(siteFonts);
  };

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <div className="root-container">
      <TopBar
        toggleCodeView={toggleCodeView}
        selectedDevice={selectedDevice}
        onDeviceSelect={handleDeviceSelect}
        onSave={saveState}
        toggleLivePreview={toggleLivePreview}
        onPublish={handlePublish}
        onLiveBuild={handleLiveBuild}
        toggleCMS={() => setShowCMSPanel(!showCMSPanel)}
        selectedContentItem={selectedContentItem}
        onContentItemSelect={updateDynamicElements}
        contentItems={contentItems}
        currentPage={currentPageId}
        pages={pages}
        zoomLevel={zoomLevel}
        setZoomLevel={setZoomLevel}
        siteId={window.location.pathname.split('/editor/')[1]}
      />

      <div className="main-container">
        {/* Left Toolbar */}
        <div className="left-section">
          <div className="left-toolbar">
            <button
              className={`toolbar-btn ${showPagesSidebar ? 'active' : ''}`}
              onClick={() => toggleSidebar("pages")}
              title="Pages"
            >
              <i className="fas fa-file-alt"></i>
              <span className="tooltip">Pages</span>
            </button>
            <button
              className={`toolbar-btn ${showElementSidebar ? 'active' : ''}`}
              onClick={() => toggleSidebar("element")}
              title="Add Elements"
            >
              <i className="fas fa-plus"></i>
              <span className="tooltip">Add Elements</span>
            </button>
            <button
              className={`toolbar-btn ${showHierarchySidebar ? 'active' : ''}`}
              onClick={() => toggleSidebar("hierarchy")}
              title="Element Hierarchy"
            >
              <i className="fas fa-sitemap"></i>
              <span className="tooltip">Hierarchy</span>
            </button>
            <button
              className={`toolbar-btn ${showAssetsSidebar ? 'active' : ''}`}
              onClick={() => toggleAssetsSidebar()}
              title="Assets Library"
            >
              <i className="fas fa-images"></i>
              <span className="tooltip">Assets</span>
            </button>
            <button
              className={`toolbar-btn ${showSectionsSidebar ? 'active' : ''}`}
              onClick={() => toggleSectionsSidebar()}
              title="Premade Sections"
            >
              <i className="fas fa-puzzle-piece"></i>
              <span className="tooltip">Sections</span>
            </button>
            <button
              className={`toolbar-btn ${showClientComponentsSidebar ? 'active' : ''}`}
              onClick={() => toggleClientComponentsSidebar()}
              title="Client Components"
            >
              <i className="fas fa-cubes"></i>
              <span className="tooltip">Components</span>
            </button>
            <button
              className={`toolbar-btn ${showCMSPanel ? 'active' : ''}`}
              onClick={() => setShowCMSPanel(!showCMSPanel)}
              title="Content Management"
            >
              <i className="fas fa-database"></i>
              <span className="tooltip">CMS</span>
            </button>
          </div>

          {showPagesSidebar && (
            <PagesSidebar
              elements={elements}
              setElements={setElements}
              pages={pages}
              currentPage={currentPageId}
              onPageChange={handlePageChange}
              onAddPage={handleAddPage}
              onDeletePage={handleDeletePage}
              onRenamePage={handleRenamePage}
              onUpdatePage={handleUpdatePage}
              isOpen={showPagesSidebar}
              selectedContentItem={selectedContentItem}
              contentItems={contentItems}
              setClassStylesRegistry={setClassStylesRegistry}
              setGlobalClassNames={setGlobalClassNames}
              setSelectedElement={setSelectedElement}
              setNewlyAddedElement={setNewlyAddedElement}
              showNotification={showNotification}
              setPages={setPages}
              currentPageId={currentPageId}
              setCurrentPageId={setCurrentPageId}
            />
          )}

          {showElementSidebar && (
            <div className="element-sidebar">
              <Elements addNewElement={addNewElement} />
            </div>
          )}

          {showHierarchySidebar && (
            <div className="hierarchy-sidebar">
              <h3>Hierarchy</h3>
              <Hierarchy
                elements={elements}
                setElements={setElements}
                selectedElement={selectedElement}
                setSelectedElement={updateSelectedElement}
                getRootElements={() => elements.filter((el) => !el.parentId)}
              />
            </div>
          )}

          {showAssetsSidebar && (
            <AssetsBar
              assets={assets}
              setAssets={setAssets}
              onSelectAsset={handleAssetSelect}
            />
          )}

          {showSectionsSidebar && (
            <SectionsSidebar
              isOpen={showSectionsSidebar}
              onClose={toggleSectionsSidebar}
              selectedCategory={selectedSectionCategory}
              setSelectedCategory={setSelectedSectionCategory}
              elements={elements}
              setElements={setElements}
              currentPageId={currentPageId}
              setClassStylesRegistry={setClassStylesRegistry}
              setGlobalClassNames={setGlobalClassNames}
              setSelectedElement={setSelectedElement}
              setNewlyAddedElement={setNewlyAddedElement}
              showNotification={showNotification}
              setShowSectionsSidebar={setShowSectionsSidebar}
            />
          )}

          {showClientComponentsSidebar && (
            <ClientComponentsSidebar
              isOpen={showClientComponentsSidebar}
              onClose={toggleClientComponentsSidebar}
              elements={elements}
              setElements={setElements}
              currentPageId={currentPageId}
              setClassStylesRegistry={setClassStylesRegistry}
              setGlobalClassNames={setGlobalClassNames}
              setSelectedElement={setSelectedElement}
              setNewlyAddedElement={setNewlyAddedElement}
              showNotification={showNotification}
              setShowClientComponentsSidebar={setShowClientComponentsSidebar}
            />
          )}

          {showCMSPanel && (
            <div className="cms-sidebar">
              <CMSPanel
                siteId={window.location.pathname.split('/editor/')[1]}
                contentTypes={cmsContentTypes}
                contentByType={cmsContent}
                onContentTypeCreate={handleContentTypeCreate}
                onContentTypeDelete={handleContentTypeDelete}
                onContentUpdate={handleCMSContentUpdate}
                assets={assets}
              />
            </div>
          )}
        </div>

        {/* Preview Wrapper with dynamic margin */}
        <div
          className="preview-wrapper"
          style={{
            marginLeft: showAssetsSidebar ? "200px" : "0",
          }}
        >
          <Preview
            elements={elements}
            selectedElement={selectedElement}
            setSelectedElement={setSelectedElement}
            getRootElements={() => elements.filter((el) => !el.parentId)}
            showElementSidebar={showElementSidebar}
            showHierarchySidebar={showHierarchySidebar}
            showAssetsSidebar={showAssetsSidebar}
            selectedDevice={selectedDevice}
            setSelectedDevice={setSelectedDevice}
            updateElementStyles={updateElementStyles}
            updateElementClassName={updateElementClassName}
            classStylesRegistry={classStylesRegistry}
            cmsContentTypes={cmsContentTypes}
            cmsContent={cmsContent}
            combinedClassStylesRegistry={combinedClassStylesRegistry}
            setElements={setElements}
            addNewElement={addNewElement}
            updateElementState={updateElementState}
            selectedContentItem={selectedContentItem}
            updateElementText={updateElementText}
            setShowAssetsSidebar={setShowAssetsSidebar}
            newlyAddedElement={newlyAddedElement}
            setNewlyAddedElement={setNewlyAddedElement} // Add this prop
            zoomLevel={zoomLevel}
            setZoomLevel={setZoomLevel}
            currentPageId={currentPageId}
            pages={pages}
          />
        </div>

        {/* Right Section */}
        <Inspector
          selectedElement={selectedElement}
          setSelectedElement={updateSelectedElement}
          updateElementStyles={updateElementStyles}
          updateElementText={updateElementText}
          updateElementClassName={updateElementClassName}
          assets={assets}
          setShowAssetsSidebar={setShowAssetsSidebar}
          selectedAssetFile={selectedAssetFile}
          handleAssetSelect={handleAssetSelect}
          elements={elements}
          setElements={setElements}  // Add this prop
          globalClassNames={globalClassNames}
          addToGlobalClassNames={addToGlobalClassNames}
          updateElementInteractions={updateElementInteractions}
          selectedDevice={selectedDevice}
          onDeviceSelect={handleDeviceSelect}
          classStylesRegistry={classStylesRegistry}
          setClassStylesRegistry={setClassStylesRegistry}
          combinedClassStylesRegistry={combinedClassStylesRegistry}
          setCombinedClassStylesRegistry={setCombinedClassStylesRegistry}  // Make sure this is passed
          appliedStylesRegistry={appliedStylesRegistry}
          setAppliedStylesRegistry={setAppliedStylesRegistry}
          pages={pages}
          currentPageId={currentPageId}
          selectedContentItem={selectedContentItem}
          cmsContentTypes={cmsContentTypes}
          cmsContent={cmsContent}
          updateElementState={updateElementState}
          setPages={setPages}
          getDefaultStyles={getDefaultStyles}
          showRichTextEditorForNewElement={showRichTextEditorForNewElement}
          siteFonts={siteFonts} // Pass site fonts to Inspector
        />
      </div>

      {showLiveBuild && (
        <LiveBuild
          pages={pages}
          siteId={window.location.pathname.split('/editor/')[1]}
          currentPageId={currentPageId}
          elements={elements}
          classStylesRegistry={classStylesRegistry}
          combinedClassStylesRegistry={combinedClassStylesRegistry}
          onClose={() => setShowLiveBuild(false)}
          cmsContentTypes={cmsContentTypes}
          cmsContent={cmsContent}
          onUpdate={setElements}
          setElements={setElements} // Add this line
        />
      )}
    </div>
  );
}

export default BuilderApp;
